import { z } from "zod";
import { AGENT_LIFECYCLE_STATUSES } from "./agent-lifecycle.js";

/**
 * Page size for the unsolicited initial agent snapshot that the daemon
 * pushes right after `server_info`. The client's normal paginated
 * `fetch_agents_request` fills in the long tail when an account has more
 * agents than this on a single host.
 */
export const InitialAgentSnapshotPageLimit = 20;
import { NotificationPreferencesSchema } from "./notification-events.js";
import { MAX_EXPLICIT_AGENT_TITLE_CHARS } from "../server/agent/agent-title-limits.js";
import { AgentProviderSchema } from "../server/agent/provider-manifest.js";
import { TOOL_CALL_ICON_NAMES } from "../server/agent/agent-sdk-types.js";
import {
  ChatCreateRequestSchema,
  ChatListRequestSchema,
  ChatInspectRequestSchema,
  ChatDeleteRequestSchema,
  ChatPostRequestSchema,
  ChatReadRequestSchema,
  ChatWaitRequestSchema,
  ChatCreateResponseSchema,
  ChatListResponseSchema,
  ChatInspectResponseSchema,
  ChatDeleteResponseSchema,
  ChatPostResponseSchema,
  ChatReadResponseSchema,
  ChatWaitResponseSchema,
} from "../server/chat/chat-rpc-schemas.js";
import {
  PreviewOpenRequestSchema,
  PreviewFrameRequestSchema,
  PreviewScrollRequestSchema,
  PreviewPickRequestSchema,
  PreviewCloseRequestSchema,
  PreviewInputRequestSchema,
  PreviewResizeRequestSchema,
  PreviewOpenResponseSchema,
  PreviewFrameResponseSchema,
  PreviewScrollResponseSchema,
  PreviewPickResponseSchema,
  PreviewCloseResponseSchema,
  PreviewResizeResponseSchema,
  PreviewFramePushSchema,
} from "../server/preview/preview-rpc-schemas.js";
import {
  ScheduleCreateRequestSchema,
  ScheduleListRequestSchema,
  ScheduleInspectRequestSchema,
  ScheduleLogsRequestSchema,
  SchedulePauseRequestSchema,
  ScheduleResumeRequestSchema,
  ScheduleDeleteRequestSchema,
  ScheduleCreateResponseSchema,
  ScheduleListResponseSchema,
  ScheduleInspectResponseSchema,
  ScheduleLogsResponseSchema,
  SchedulePauseResponseSchema,
  ScheduleResumeResponseSchema,
  ScheduleDeleteResponseSchema,
} from "../server/schedule/rpc-schemas.js";
import {
  LoopRunRequestSchema,
  LoopListRequestSchema,
  LoopInspectRequestSchema,
  LoopLogsRequestSchema,
  LoopStopRequestSchema,
  LoopRunResponseSchema,
  LoopListResponseSchema,
  LoopInspectResponseSchema,
  LoopLogsResponseSchema,
  LoopStopResponseSchema,
} from "../server/loop/rpc-schemas.js";
import {
  QuestRunRequestSchema,
  QuestListRequestSchema,
  QuestInspectRequestSchema,
  QuestStopRequestSchema,
  QuestRunResponseSchema,
  QuestListResponseSchema,
  QuestInspectResponseSchema,
  QuestStopResponseSchema,
  QuestStreamMessageSchema,
  RolesListRequestSchema,
  RolesListResponseSchema,
  RolesListScopeRequestSchema,
  RolesListScopeResponseSchema,
  RoleCreateRequestSchema,
  RoleCreateResponseSchema,
  RoleWriteFrontmatterRequestSchema,
  RoleWriteFrontmatterResponseSchema,
  RoleMoveRequestSchema,
  RoleMoveResponseSchema,
  BrandsListScopeRequestSchema,
  BrandsListScopeResponseSchema,
  BrandCreateRequestSchema,
  BrandCreateResponseSchema,
  BrandWriteFrontmatterRequestSchema,
  BrandWriteFrontmatterResponseSchema,
  BrandAssetCopyRequestSchema,
  BrandAssetCopyResponseSchema,
  BrandAssetUploadRequestSchema,
  BrandAssetUploadResponseSchema,
  BrandAssetDeleteRequestSchema,
  BrandAssetDeleteResponseSchema,
  BrandAssetRenameRequestSchema,
  BrandAssetRenameResponseSchema,
  BrandGenerateTokensRequestSchema,
  BrandGenerateTokensResponseSchema,
  BrandTokenGenerationStatusRequestSchema,
  BrandTokenGenerationStatusResponseSchema,
  BrandGenerateLayoutRequestSchema,
  BrandGenerateLayoutResponseSchema,
  BrandLayoutQaNextRequestSchema,
  BrandLayoutQaNextResponseSchema,
  BrandGenerateWireframeRequestSchema,
  BrandGenerateWireframeResponseSchema,
  BrandAnalyzePhotographyRequestSchema,
  BrandAnalyzePhotographyResponseSchema,
  RightFontLibraryRequestSchema,
  RightFontLibraryResponseSchema,
  GoogleFontsCatalogRequestSchema,
  GoogleFontsCatalogResponseSchema,
  GoogleFontsDownloadRequestSchema,
  GoogleFontsDownloadResponseSchema,
} from "../server/quest/rpc-schemas.js";
// ---------------------------------------------------------------------------
// Mutable daemon config schemas (shared between server store and client)
// ---------------------------------------------------------------------------

export const MutableDaemonConfigSchema = z
  .object({
    mcp: z
      .object({
        injectIntoAgents: z.boolean(),
      })
      .passthrough(),
    identity: z
      .object({
        // Daemon display icon (curated Lucide icon id, e.g. "laptop",
        // "server", "cloud"). Client UI picks from a known set; an
        // unknown string is treated as unset by the client.
        icon: z.string().optional(),
      })
      .passthrough()
      .optional(),
    chrome: z
      .object({
        // When false, agents on this host won't have the claude-in-chrome
        // tools injected. Set to false on hosts where the client's browser
        // should not be controlled (e.g. headless/server-only hosts).
        enabled: z.boolean(),
      })
      .passthrough()
      .optional(),
    playwright: z
      .object({
        // When false, agents on this host won't have the Playwright MCP
        // server injected.
        enabled: z.boolean(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

export const MutableDaemonConfigPatchSchema = z
  .object({
    mcp: MutableDaemonConfigSchema.shape.mcp.partial().optional(),
    identity: z
      .object({
        icon: z.string().optional(),
      })
      .partial()
      .passthrough()
      .optional(),
    chrome: MutableDaemonConfigSchema.shape.chrome,
    playwright: MutableDaemonConfigSchema.shape.playwright,
  })
  .partial()
  .passthrough();

export type MutableDaemonConfig = z.infer<typeof MutableDaemonConfigSchema>;
export type MutableDaemonConfigPatch = z.infer<typeof MutableDaemonConfigPatchSchema>;
import type { LiteralUnion } from "./literal-union.js";
import type {
  AgentCapabilityFlags,
  AgentModelDefinition,
  AgentMode,
  AgentPermissionRequest,
  AgentPermissionResponse,
  AgentPersistenceHandle,
  ProviderSnapshotEntry,
  ProviderStatus,
  AgentRuntimeInfo,
  AgentTimelineItem,
  ToolCallDetail,
  ToolCallTimelineItem,
  AgentUsage,
} from "../server/agent/agent-sdk-types.js";

export const AgentStatusSchema = z.enum(AGENT_LIFECYCLE_STATUSES);

const AgentModeSchema: z.ZodType<AgentMode> = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  icon: z.string().optional(),
  colorTier: z.string().optional(),
});

const ProviderStatusSchema: z.ZodType<ProviderStatus> = z.enum([
  "ready",
  "loading",
  "error",
  "unavailable",
]);

const AgentSelectOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  isDefault: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const AgentFeatureToggleSchema = z.object({
  type: z.literal("toggle"),
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  tooltip: z.string().optional(),
  icon: z.string().optional(),
  value: z.boolean(),
});

export const AgentFeatureSelectSchema = z.object({
  type: z.literal("select"),
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  tooltip: z.string().optional(),
  icon: z.string().optional(),
  value: z.string().nullable(),
  options: z.array(AgentSelectOptionSchema),
});

export const AgentFeatureSchema = z.discriminatedUnion("type", [
  AgentFeatureToggleSchema,
  AgentFeatureSelectSchema,
]);

const AgentModelDefinitionSchema: z.ZodType<AgentModelDefinition> = z.object({
  provider: AgentProviderSchema,
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  isDefault: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
  thinkingOptions: z.array(AgentSelectOptionSchema).optional(),
  defaultThinkingOptionId: z.string().optional(),
});

const ProviderSnapshotEntrySchema: z.ZodType<ProviderSnapshotEntry> = z.object({
  provider: AgentProviderSchema,
  status: ProviderStatusSchema,
  error: z.string().optional(),
  models: z.array(AgentModelDefinitionSchema).optional(),
  modes: z.array(AgentModeSchema).optional(),
  fetchedAt: z.string().optional(),
  label: z.string().optional(),
  description: z.string().optional(),
  defaultModeId: z.string().nullable().optional(),
});

const AgentCapabilityFlagsSchema: z.ZodType<AgentCapabilityFlags> = z.object({
  supportsStreaming: z.boolean(),
  supportsSessionPersistence: z.boolean(),
  supportsDynamicModes: z.boolean(),
  supportsMcpServers: z.boolean(),
  supportsReasoningStream: z.boolean(),
  supportsToolInvocations: z.boolean(),
});

const AgentRateLimitSchema = z.object({
  rateLimitType: z.enum([
    "five_hour",
    "seven_day",
    "seven_day_opus",
    "seven_day_sonnet",
    "overage",
  ]),
  utilization: z.number(),
  resetsAt: z.number().optional(),
});

const AgentUsageSchema: z.ZodType<AgentUsage> = z.object({
  inputTokens: z.number().optional(),
  cachedInputTokens: z.number().optional(),
  outputTokens: z.number().optional(),
  totalCostUsd: z.number().optional(),
  contextWindowMaxTokens: z.number().optional(),
  contextWindowUsedTokens: z.number().optional(),
  rateLimits: z.array(AgentRateLimitSchema).optional(),
});

const McpStdioServerConfigSchema = z.object({
  type: z.literal("stdio"),
  command: z.string(),
  args: z.array(z.string()).optional(),
  env: z.record(z.string()).optional(),
});

const McpHttpServerConfigSchema = z.object({
  type: z.literal("http"),
  url: z.string(),
  headers: z.record(z.string()).optional(),
});

const McpSseServerConfigSchema = z.object({
  type: z.literal("sse"),
  url: z.string(),
  headers: z.record(z.string()).optional(),
});

const McpServerConfigSchema = z.discriminatedUnion("type", [
  McpStdioServerConfigSchema,
  McpHttpServerConfigSchema,
  McpSseServerConfigSchema,
]);

const AgentSessionConfigSchema = z.object({
  provider: AgentProviderSchema,
  cwd: z.string(),
  modeId: z.string().optional(),
  model: z.string().optional(),
  thinkingOptionId: z.string().optional(),
  featureValues: z.record(z.unknown()).optional(),
  title: z.string().trim().min(1).max(MAX_EXPLICIT_AGENT_TITLE_CHARS).optional().nullable(),
  approvalPolicy: z.string().optional(),
  sandboxMode: z.string().optional(),
  networkAccess: z.boolean().optional(),
  webSearch: z.boolean().optional(),
  extra: z
    .object({
      codex: z.record(z.unknown()).optional(),
      claude: z.record(z.unknown()).optional(),
    })
    .partial()
    .optional(),
  systemPrompt: z.string().optional(),
  mcpServers: z.record(McpServerConfigSchema).optional(),
});

const AgentPermissionUpdateSchema = z.record(z.unknown());
const AgentPermissionActionSchema = z.object({
  id: z.string(),
  label: z.string(),
  behavior: z.enum(["allow", "deny"]),
  variant: z.enum(["primary", "secondary", "danger"]).optional(),
  intent: z.enum(["implement", "implement_resume", "dismiss"]).optional(),
});

export const AgentPermissionResponseSchema: z.ZodType<AgentPermissionResponse> = z.union([
  z.object({
    behavior: z.literal("allow"),
    selectedActionId: z.string().optional(),
    updatedInput: z.record(z.unknown()).optional(),
    updatedPermissions: z.array(AgentPermissionUpdateSchema).optional(),
  }),
  z.object({
    behavior: z.literal("deny"),
    selectedActionId: z.string().optional(),
    message: z.string().optional(),
    interrupt: z.boolean().optional(),
  }),
]);

export const AgentPermissionRequestPayloadSchema: z.ZodType<AgentPermissionRequest> = z.object({
  id: z.string(),
  provider: AgentProviderSchema,
  name: z.string(),
  kind: z.enum(["tool", "plan", "question", "mode", "other"]),
  title: z.string().optional(),
  description: z.string().optional(),
  input: z.record(z.unknown()).optional(),
  detail: z.lazy(() => ToolCallDetailPayloadSchema).optional(),
  suggestions: z.array(AgentPermissionUpdateSchema).optional(),
  actions: z.array(AgentPermissionActionSchema).optional(),
  metadata: z.record(z.unknown()).optional(),
});

const UnknownValueSchema = z.union([
  z.null(),
  z.boolean(),
  z.number(),
  z.string(),
  z.array(z.unknown()),
  z.object({}).passthrough(),
]);

const NonNullUnknownSchema = z.union([
  z.boolean(),
  z.number(),
  z.string(),
  z.array(z.unknown()),
  z.object({}).passthrough(),
]);

const WorktreeSetupCommandSnapshotSchema = z.object({
  index: z.number().int().positive(),
  command: z.string(),
  cwd: z.string(),
  log: z.string(),
  status: z.enum(["running", "completed", "failed"]),
  exitCode: z.number().nullable(),
  durationMs: z.number().nonnegative().optional(),
});

const WorktreeSetupDetailPayloadSchema = z.object({
  type: z.literal("worktree_setup"),
  worktreePath: z.string(),
  branchName: z.string(),
  log: z.string(),
  commands: z.array(WorktreeSetupCommandSnapshotSchema),
  truncated: z.boolean().optional(),
});

const ToolCallDetailPayloadSchema: z.ZodType<ToolCallDetail> = z.discriminatedUnion("type", [
  WorktreeSetupDetailPayloadSchema,
  z.object({
    type: z.literal("shell"),
    command: z.string(),
    description: z.string().optional(),
    cwd: z.string().optional(),
    output: z.string().optional(),
    exitCode: z.number().nullable().optional(),
  }),
  z.object({
    type: z.literal("read"),
    filePath: z.string(),
    content: z.string().optional(),
    offset: z.number().optional(),
    limit: z.number().optional(),
  }),
  z.object({
    type: z.literal("edit"),
    filePath: z.string(),
    oldString: z.string().optional(),
    newString: z.string().optional(),
    unifiedDiff: z.string().optional(),
  }),
  z.object({
    type: z.literal("write"),
    filePath: z.string(),
    content: z.string().optional(),
  }),
  z.object({
    type: z.literal("search"),
    query: z.string(),
    toolName: z.enum(["search", "grep", "glob", "web_search"]).optional(),
    content: z.string().optional(),
    filePaths: z.array(z.string()).optional(),
    webResults: z
      .array(
        z.object({
          title: z.string(),
          url: z.string(),
        }),
      )
      .optional(),
    annotations: z.array(z.string()).optional(),
    numFiles: z.number().optional(),
    numMatches: z.number().optional(),
    durationMs: z.number().optional(),
    durationSeconds: z.number().optional(),
    truncated: z.boolean().optional(),
    mode: z.enum(["content", "files_with_matches", "count"]).optional(),
  }),
  z.object({
    type: z.literal("fetch"),
    url: z.string(),
    prompt: z.string().optional(),
    result: z.string().optional(),
    code: z.number().optional(),
    codeText: z.string().optional(),
    bytes: z.number().optional(),
    durationMs: z.number().optional(),
  }),
  z.object({
    type: z.literal("sub_agent"),
    subAgentType: z.string().optional(),
    description: z.string().optional(),
    log: z.string().optional(),
    actions: z
      .array(
        z.object({
          index: z.number().int().positive(),
          toolName: z.string(),
          summary: z.string().optional(),
        }),
      )
      .optional(),
  }),
  z.object({
    type: z.literal("plain_text"),
    label: z.string().optional(),
    text: z.string().optional(),
    icon: z.enum(TOOL_CALL_ICON_NAMES).optional(),
  }),
  z.object({
    type: z.literal("plan"),
    text: z.string(),
  }),
  z.object({
    type: z.literal("image"),
    toolName: z.string().optional(),
    images: z.array(
      z.object({
        mimeType: z.string(),
        data: z.string(),
      }),
    ),
  }),
  z.object({
    type: z.literal("unknown"),
    input: UnknownValueSchema,
    output: UnknownValueSchema,
  }),
]);

const ToolCallBasePayloadSchema = z
  .object({
    type: z.literal("tool_call"),
    callId: z.string(),
    name: z.string(),
    detail: ToolCallDetailPayloadSchema,
    metadata: z.record(z.unknown()).optional(),
  })
  .strict();

const ToolCallRunningPayloadSchema = ToolCallBasePayloadSchema.extend({
  status: z.literal("running"),
  error: z.null(),
});

const ToolCallCompletedPayloadSchema = ToolCallBasePayloadSchema.extend({
  status: z.literal("completed"),
  error: z.null(),
});

const ToolCallFailedPayloadSchema = ToolCallBasePayloadSchema.extend({
  status: z.literal("failed"),
  error: NonNullUnknownSchema,
});

const ToolCallCanceledPayloadSchema = ToolCallBasePayloadSchema.extend({
  status: z.literal("canceled"),
  error: z.null(),
});

const ToolCallTimelineItemPayloadSchema: z.ZodType<ToolCallTimelineItem, z.ZodTypeDef, unknown> =
  z.union([
    ToolCallRunningPayloadSchema,
    ToolCallCompletedPayloadSchema,
    ToolCallFailedPayloadSchema,
    ToolCallCanceledPayloadSchema,
  ]);

export const AgentTimelineItemPayloadSchema: z.ZodType<AgentTimelineItem, z.ZodTypeDef, unknown> =
  z.union([
    z.object({
      type: z.literal("user_message"),
      text: z.string(),
      messageId: z.string().optional(),
      // Real Claude transcript UUID, stamped on the row when the SDK echoes
      // the user turn back from JSONL. Optional → old daemons that don't yet
      // populate it and old clients that don't read it both keep working.
      claudeUuid: z.string().optional(),
      // Bytes-stripped attachment metadata so a reload re-renders the chip
      // without round-tripping image/file bytes through the timeline. Both
      // optional → backward-compatible with old daemons (silently strip) and
      // old clients (silently ignore). Inlined (not factored to a const)
      // because they're only used in this one place and forward-declaring
      // them would force a top-level reorder.
      images: z
        .array(
          z.object({
            id: z.string(),
            mimeType: z.string(),
            fileName: z.string().optional(),
            byteSize: z.number().int().nonnegative().optional(),
            // Daemon-side pointer for fetch-by-id. Optional → old daemons
            // (which only emit local-cache-friendly metadata) keep working;
            // old clients see the field and ignore it.
            daemonRef: z
              .object({
                kind: z.enum(["image", "file"]),
                attachmentId: z.string(),
                relativePath: z.string(),
              })
              .optional(),
          }),
        )
        .optional(),
      files: z
        .array(
          z.object({
            id: z.string(),
            fileName: z.string(),
            mimeType: z.string(),
            size: z.number().int().nonnegative(),
            daemonRef: z
              .object({
                kind: z.enum(["image", "file"]),
                attachmentId: z.string(),
                relativePath: z.string(),
              })
              .optional(),
          }),
        )
        .optional(),
      // Marker for daemon-synthesized text (e.g. "Sent 1 image" when the
      // user typed nothing). The chat bubble hides the text when this is
      // true; the LLM still sees it for cache_control compliance.
      textIsSynthesized: z.boolean().optional(),
    }),
    z.object({
      type: z.literal("assistant_message"),
      text: z.string(),
    }),
    z.object({
      type: z.literal("reasoning"),
      text: z.string(),
    }),
    ToolCallTimelineItemPayloadSchema,
    z.object({
      type: z.literal("todo"),
      items: z.array(
        z.object({
          text: z.string(),
          completed: z.boolean(),
        }),
      ),
    }),
    z.object({
      type: z.literal("error"),
      message: z.string(),
    }),
    z.object({
      type: z.literal("compaction"),
      status: z.enum(["loading", "completed"]),
      trigger: z.enum(["auto", "manual"]).optional(),
      preTokens: z.number().optional(),
    }),
  ]);

export const AgentStreamEventPayloadSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("thread_started"),
    sessionId: z.string(),
    provider: AgentProviderSchema,
  }),
  z.object({
    type: z.literal("turn_started"),
    provider: AgentProviderSchema,
  }),
  z.object({
    type: z.literal("turn_completed"),
    provider: AgentProviderSchema,
    usage: AgentUsageSchema.optional(),
  }),
  z.object({
    type: z.literal("turn_failed"),
    provider: AgentProviderSchema,
    error: z.string(),
    code: z.string().optional(),
    diagnostic: z.string().optional(),
  }),
  z.object({
    type: z.literal("turn_canceled"),
    provider: AgentProviderSchema,
    reason: z.string(),
  }),
  z.object({
    type: z.literal("timeline"),
    provider: AgentProviderSchema,
    item: AgentTimelineItemPayloadSchema,
  }),
  z.object({
    type: z.literal("permission_requested"),
    provider: AgentProviderSchema,
    request: AgentPermissionRequestPayloadSchema,
  }),
  z.object({
    type: z.literal("permission_resolved"),
    provider: AgentProviderSchema,
    requestId: z.string(),
    resolution: AgentPermissionResponseSchema,
  }),
  z.object({
    type: z.literal("attention_required"),
    provider: AgentProviderSchema,
    reason: z.enum(["finished", "error", "permission"]),
    timestamp: z.string(),
    shouldNotify: z.boolean(),
    notification: z
      .object({
        title: z.string(),
        body: z.string(),
        data: z.object({
          serverId: z.string(),
          agentId: z.string(),
          reason: z.enum(["finished", "error", "permission"]),
        }),
      })
      .optional(),
  }),
  z.object({
    // Streams incremental file content while the agent writes a file.
    // Live-only — not persisted; the final `tool_call` timeline item is
    // the structural record. Clients use it to render the write in real
    // time in the file editor without waiting for a poll.
    type: z.literal("file_chunk"),
    provider: AgentProviderSchema,
    toolUseId: z.string(),
    filePath: z.string(),
    content: z.string(),
    isComplete: z.boolean(),
    sequenceId: z.number(),
  }),
]);

const AgentPersistenceHandleSchema: z.ZodType<AgentPersistenceHandle | null> = z
  .object({
    provider: AgentProviderSchema,
    sessionId: z.string(),
    nativeHandle: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .nullable();

const AgentRuntimeInfoSchema: z.ZodType<AgentRuntimeInfo> = z.object({
  provider: AgentProviderSchema,
  sessionId: z.string().nullable(),
  model: z.string().nullable().optional(),
  thinkingOptionId: z.string().nullable().optional(),
  modeId: z.string().nullable().optional(),
  extra: z.record(z.unknown()).optional(),
});

export const AgentSnapshotPayloadSchema = z.object({
  id: z.string(),
  provider: AgentProviderSchema,
  cwd: z.string(),
  model: z.string().nullable(),
  features: z.array(AgentFeatureSchema).optional(),
  thinkingOptionId: z.string().nullable().optional(),
  effectiveThinkingOptionId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastUserMessageAt: z.string().nullable(),
  // Mirror of lastUserMessageAt for the assistant side — stamped when a
  // turn completes. Used by the auth-server's session projection so the
  // Recents screen can rank "most-recent activity" correctly for
  // long-running autonomous agents (which keep replying without new
  // user input). Optional for backward compat with old mobile clients
  // that don't know this field.
  lastAssistantMessageAt: z.string().nullable().optional(),
  status: AgentStatusSchema,
  capabilities: AgentCapabilityFlagsSchema,
  currentModeId: z.string().nullable(),
  availableModes: z.array(AgentModeSchema),
  pendingPermissions: z.array(AgentPermissionRequestPayloadSchema),
  persistence: AgentPersistenceHandleSchema.nullable(),
  runtimeInfo: AgentRuntimeInfoSchema.optional(),
  lastUsage: AgentUsageSchema.optional(),
  lastError: z.string().optional(),
  title: z.string().nullable(),
  labels: z.record(z.string()).default({}),
  requiresAttention: z.boolean().optional(),
  attentionReason: z.enum(["finished", "error", "permission"]).nullable().optional(),
  attentionTimestamp: z.string().nullable().optional(),
  archivedAt: z.string().nullable().optional(),
  // Fork lineage — optional for backward compat with pre-fork agents.
  parentAgentId: z.string().optional(),
  forkedFromMessageUuid: z.string().optional(),
  /**
   * True when the agent is a system-spawned helper (orchestrator queens,
   * handoff workers). Optional for backward compat — older clients ignore
   * the field. Newer clients filter these out of workspace tab lists / agent
   * lists at display time. The agent itself is a real, full-featured session
   * in every other respect; `internal` is a UI visibility hint, nothing more.
   */
  internal: z.boolean().optional(),
  /**
   * Multi-tenant ownership (Phase 2c/4). Surfaces the auth-server user-id
   * of the agent's creator so the app can render an owner badge and
   * detect "shared with me" state (owner !== current user). Optional/
   * nullable for legacy agents created before per-agent ownership
   * existed — those render without a badge.
   *
   */
  ownerUserId: z.string().nullable().optional(),
  /**
   * @deprecated Phase 7 — removed from daemon snapshots. Tab stack
   * placement now lives on RemoteTab.stackId (auth-server). Field kept
   * as optional so old client builds that read it don't crash.
   */
  stackId: z.string().nullable().optional(),
  /**
   * Per-session share grants. Present on agent snapshots so the owner's
   * UI can display who currently has access. Optional for backward compat.
   */
  sessionShares: z
    .array(
      z.object({
        userId: z.string(),
        orgId: z.string(),
        grantedAt: z.string(),
      }),
    )
    .optional(),
});

export type AgentSnapshotPayload = z.infer<typeof AgentSnapshotPayloadSchema>;

export type AgentStreamEventPayload = z.infer<typeof AgentStreamEventPayloadSchema>;

// ============================================================================
// Session Inbound Messages (Session receives these)
// ============================================================================

export const VoiceAudioChunkMessageSchema = z.object({
  type: z.literal("voice_audio_chunk"),
  audio: z.string(), // base64 encoded
  format: z.string(),
  isLast: z.boolean(),
});

export const AbortRequestMessageSchema = z.object({
  type: z.literal("abort_request"),
});

export const AudioPlayedMessageSchema = z.object({
  type: z.literal("audio_played"),
  id: z.string(),
});

const AgentDirectoryFilterSchema = z.object({
  labels: z.record(z.string()).optional(),
  projectKeys: z.array(z.string()).optional(),
  statuses: z.array(AgentStatusSchema).optional(),
  includeArchived: z.boolean().optional(),
  requiresAttention: z.boolean().optional(),
  thinkingOptionId: z.string().nullable().optional(),
});

export const DeleteAgentRequestMessageSchema = z.object({
  type: z.literal("delete_agent_request"),
  agentId: z.string(),
  requestId: z.string(),
});

export const ArchiveAgentRequestMessageSchema = z.object({
  type: z.literal("archive_agent_request"),
  agentId: z.string(),
  requestId: z.string(),
});

export const ForkAgentRequestMessageSchema = z.object({
  type: z.literal("fork_agent_request"),
  parentAgentId: z.string(),
  // Client-known id for the user message to fork at. May be a appostle-internal id
  // (e.g. "msg_<timestamp>_<random>") rather than a Claude UUID. The daemon
  // will translate using forkPointUserMessageIndex when that's set.
  forkPointUuid: z.string(),
  // 0-based index of the user message within the conversation's user turns
  // (counted from oldest to newest). Used by the daemon to resolve the real
  // Claude session UUID via SDK getSessionMessages().
  forkPointUserMessageIndex: z.number().int().nonnegative().optional(),
  requestId: z.string(),
});

export const RewindAgentRequestMessageSchema = z.object({
  type: z.literal("rewind_agent_request"),
  agentId: z.string(),
  // Client-known id for the user message to rewind to. Same translation rules
  // as fork — may be a appostle-internal id (e.g. "msg_<ts>_<rand>") that the
  // daemon resolves via `rewindPointUserMessageIndex`.
  rewindPointUuid: z.string(),
  rewindPointUserMessageIndex: z.number().int().nonnegative().optional(),
  requestId: z.string(),
});

export const CloseItemsRequestMessageSchema = z.object({
  type: z.literal("close_items_request"),
  agentIds: z.array(z.string()).default([]),
  terminalIds: z.array(z.string()).default([]),
  requestId: z.string(),
});

export const UpdateAgentRequestMessageSchema = z.object({
  type: z.literal("update_agent_request"),
  agentId: z.string(),
  name: z.string().optional(),
  labels: z.record(z.string()).optional(),
  internal: z.boolean().optional(),
  /**
   * @deprecated Phase 7 — ignored by daemon. Tab stacks live on auth-server.
   * Accepted so old client builds don't break.
   */
  stackId: z.string().nullable().optional(),
  requestId: z.string(),
});

export const ShareSessionRequestMessageSchema = z.object({
  type: z.literal("share_session_request"),
  agentId: z.string(),
  userId: z.string(),
  orgId: z.string(),
  requestId: z.string(),
});

export const UnshareSessionRequestMessageSchema = z.object({
  type: z.literal("unshare_session_request"),
  agentId: z.string(),
  userId: z.string(),
  orgId: z.string(),
  requestId: z.string(),
});

export const ShareSessionResponseMessageSchema = z.object({
  type: z.literal("share_session_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    sessionShares: z.array(
      z.object({
        userId: z.string(),
        orgId: z.string(),
        grantedAt: z.string(),
      }),
    ),
    error: z.string().nullable(),
  }),
});

export const UnshareSessionResponseMessageSchema = z.object({
  type: z.literal("unshare_session_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    sessionShares: z.array(
      z.object({
        userId: z.string(),
        orgId: z.string(),
        grantedAt: z.string(),
      }),
    ),
    error: z.string().nullable(),
  }),
});

// ============================================================================
// Tab stacks — per-workspace, per-user named containers for sessions.
// See .plans/tab-stacks.md for the model.
// ============================================================================

export const WorkspaceStackSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type WorkspaceStack = z.infer<typeof WorkspaceStackSchema>;

export const WorkspaceStacksStateSchema = z.object({
  type: z.literal("workspace_stacks_state"),
  payload: z.object({
    workspaceId: z.string(),
    stacks: z.array(WorkspaceStackSchema),
    activeStackId: z.string().nullable(),
  }),
});

export type WorkspaceStacksStateMessage = z.infer<typeof WorkspaceStacksStateSchema>;

export const StackCreateRequestMessageSchema = z.object({
  type: z.literal("stack_create_request"),
  workspaceId: z.string(),
  name: z.string().min(1).max(64),
  requestId: z.string(),
});

export const StackCreateResponseMessageSchema = z.object({
  type: z.literal("stack_create_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    stack: WorkspaceStackSchema.optional(),
    error: z.string().nullable().optional(),
  }),
});

export const StackRenameRequestMessageSchema = z.object({
  type: z.literal("stack_rename_request"),
  workspaceId: z.string(),
  stackId: z.string(),
  name: z.string().min(1).max(64),
  requestId: z.string(),
});

export const StackRenameResponseMessageSchema = z.object({
  type: z.literal("stack_rename_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const StackDeleteRequestMessageSchema = z.object({
  type: z.literal("stack_delete_request"),
  workspaceId: z.string(),
  stackId: z.string(),
  requestId: z.string(),
});

export const StackDeleteResponseMessageSchema = z.object({
  type: z.literal("stack_delete_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    movedAgentCount: z.number().int().nonnegative().optional(),
    error: z.string().nullable().optional(),
  }),
});

export const StackReorderRequestMessageSchema = z.object({
  type: z.literal("stack_reorder_request"),
  workspaceId: z.string(),
  orderedStackIds: z.array(z.string()),
  requestId: z.string(),
});

export const StackReorderResponseMessageSchema = z.object({
  type: z.literal("stack_reorder_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const StackSetActiveRequestMessageSchema = z.object({
  type: z.literal("stack_set_active_request"),
  workspaceId: z.string(),
  stackId: z.string().nullable(),
  requestId: z.string(),
});

export const StackSetActiveResponseMessageSchema = z.object({
  type: z.literal("stack_set_active_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const SetVoiceModeMessageSchema = z.object({
  type: z.literal("set_voice_mode"),
  enabled: z.boolean(),
  agentId: z.string().optional(),
  requestId: z.string().optional(),
});

export const GitHubPrAttachmentSchema = z.object({
  type: z.literal("github_pr"),
  mimeType: z.literal("application/github-pr"),
  number: z.number().int().positive(),
  title: z.string(),
  url: z.string(),
  body: z.string().nullable().optional(),
  baseRefName: z.string().nullable().optional(),
  headRefName: z.string().nullable().optional(),
});

export const GitHubIssueAttachmentSchema = z.object({
  type: z.literal("github_issue"),
  mimeType: z.literal("application/github-issue"),
  number: z.number().int().positive(),
  title: z.string(),
  url: z.string(),
  body: z.string().nullable().optional(),
});

// GitLab attachments mirror the GitHub variants but use GitLab's terminology
// (iid for issue/MR number, "mr" for merge request). Adding to the
// discriminated union is backward-compatible: `normalizeAgentAttachments`
// drops items the schema doesn't recognize, so older daemons silently ignore
// these rather than rejecting the whole message.
export const GitLabMrAttachmentSchema = z.object({
  type: z.literal("gitlab_mr"),
  mimeType: z.literal("application/gitlab-mr"),
  iid: z.number().int().positive(),
  title: z.string(),
  url: z.string(),
  body: z.string().nullable().optional(),
  sourceBranch: z.string().nullable().optional(),
  targetBranch: z.string().nullable().optional(),
});

export const GitLabIssueAttachmentSchema = z.object({
  type: z.literal("gitlab_issue"),
  mimeType: z.literal("application/gitlab-issue"),
  iid: z.number().int().positive(),
  title: z.string(),
  url: z.string(),
  body: z.string().nullable().optional(),
});

export const AgentAttachmentSchema = z.discriminatedUnion("type", [
  GitHubPrAttachmentSchema,
  GitHubIssueAttachmentSchema,
  GitLabMrAttachmentSchema,
  GitLabIssueAttachmentSchema,
]);

function normalizeAgentAttachments(input: unknown): AgentAttachment[] {
  if (!Array.isArray(input)) {
    return [];
  }
  const normalized: AgentAttachment[] = [];
  for (const item of input) {
    const parsed = AgentAttachmentSchema.safeParse(item);
    if (parsed.success) {
      normalized.push(parsed.data);
    }
  }
  return normalized;
}

const AgentAttachmentsSchema = z.unknown().transform(normalizeAgentAttachments).optional();

const ImageAttachmentSchema = z.object({
  data: z.string(), // base64 encoded image
  mimeType: z.string(), // e.g., "image/jpeg", "image/png"
  // Optional client-side identity. When provided, the daemon echoes these
  // (without bytes) on the `user_message` timeline event so a reload can
  // re-render the chip. All optional → backward-compatible: old clients
  // omit them, old daemons strip them.
  id: z.string().optional(),
  fileName: z.string().optional(),
  byteSize: z.number().int().nonnegative().optional(),
});

// Generic file attachments for chat messages (PDFs, archives, .sql/.db, code, etc.).
// Inline base64 transport. Backward-compatible: `files` is optional everywhere it
// appears, so old clients that omit it work as before, and old daemons that don't
// know about `files` silently strip it (Zod object schemas drop unknown keys).
// Size enforcement happens at the daemon storage layer, not in this schema.
export const FileAttachmentSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(255),
  size: z.number().int().nonnegative(),
  data: z.string(), // base64-encoded bytes
});

// Public manifest entry returned by list_session_uploads_response. Does NOT
// include the file's bytes — clients fetch contents via the existing
// file_download_token_request flow using `relativePath`.
export const SessionUploadSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  size: z.number().int().nonnegative(),
  createdAt: z.string(), // ISO-8601 UTC timestamp
  messageId: z.string().nullable(),
  // Path relative to the agent's workspace root, e.g.
  // ".appostle/tmp/uploaded_files/<id>__report.sql". Always uses POSIX separators.
  relativePath: z.string(),
});

export const SendAgentMessageSchema = z.object({
  type: z.literal("send_agent_message"),
  agentId: z.string(),
  text: z.string(),
  messageId: z.string().optional(), // Client-provided ID for deduplication
  images: z.array(ImageAttachmentSchema).optional(),
  files: z.array(FileAttachmentSchema).optional(),
  attachments: AgentAttachmentsSchema,
});

// ============================================================================
// Agent RPCs (requestId-correlated)
// ============================================================================

export const FetchAgentsRequestMessageSchema = z.object({
  type: z.literal("fetch_agents_request"),
  requestId: z.string(),
  filter: AgentDirectoryFilterSchema.optional(),
  sort: z
    .array(
      z.object({
        key: z.enum(["status_priority", "created_at", "updated_at", "title"]),
        direction: z.enum(["asc", "desc"]),
      }),
    )
    .optional(),
  page: z
    .object({
      limit: z.number().int().positive().max(200),
      cursor: z.string().min(1).optional(),
    })
    .optional(),
  subscribe: z
    .object({
      subscriptionId: z.string().optional(),
    })
    .optional(),
});

const WorkspaceStateBucketSchema = z.enum([
  "needs_input",
  "failed",
  "running",
  "attention",
  "done",
]);

export const FetchWorkspacesRequestMessageSchema = z.object({
  type: z.literal("fetch_workspaces_request"),
  requestId: z.string(),
  filter: z
    .object({
      query: z.string().optional(),
      projectId: z.string().optional(),
      idPrefix: z.string().optional(),
    })
    .optional(),
  sort: z
    .array(
      z.object({
        key: z.enum(["status_priority", "activity_at", "name", "project_id"]),
        direction: z.enum(["asc", "desc"]),
      }),
    )
    .optional(),
  page: z
    .object({
      limit: z.number().int().positive().max(200),
      cursor: z.string().min(1).optional(),
    })
    .optional(),
  subscribe: z
    .object({
      subscriptionId: z.string().optional(),
    })
    .optional(),
});

export const FetchAgentRequestMessageSchema = z.object({
  type: z.literal("fetch_agent_request"),
  requestId: z.string(),
  /** Accepts full ID, unique prefix, or exact full title (server resolves). */
  agentId: z.string(),
});

export const SendAgentMessageRequestSchema = z.object({
  type: z.literal("send_agent_message_request"),
  requestId: z.string(),
  /** Accepts full ID, unique prefix, or exact full title (server resolves). */
  agentId: z.string(),
  text: z.string(),
  messageId: z.string().optional(), // Client-provided ID for deduplication
  images: z.array(ImageAttachmentSchema).optional(),
  files: z.array(FileAttachmentSchema).optional(),
  attachments: AgentAttachmentsSchema,
});

export const WaitForFinishRequestSchema = z.object({
  type: z.literal("wait_for_finish_request"),
  requestId: z.string(),
  /** Accepts full ID, unique prefix, or exact full title (server resolves). */
  agentId: z.string(),
  timeoutMs: z.number().int().positive().optional(),
});

export const GetDaemonConfigRequestMessageSchema = z.object({
  type: z.literal("get_daemon_config_request"),
  requestId: z.string(),
});

export const SetDaemonConfigRequestMessageSchema = z.object({
  type: z.literal("set_daemon_config_request"),
  requestId: z.string(),
  config: MutableDaemonConfigPatchSchema,
});

// ============================================================================
// Dictation Streaming (lossless, resumable)
// ============================================================================

export const DictationStreamStartMessageSchema = z.object({
  type: z.literal("dictation_stream_start"),
  dictationId: z.string(),
  format: z.string(), // e.g. "audio/pcm;rate=16000;bits=16"
});

export const DictationStreamChunkMessageSchema = z.object({
  type: z.literal("dictation_stream_chunk"),
  dictationId: z.string(),
  seq: z.number().int().nonnegative(),
  audio: z.string(), // base64 encoded chunk
  format: z.string(), // e.g. "audio/pcm;rate=16000;bits=16"
});

export const DictationStreamFinishMessageSchema = z.object({
  type: z.literal("dictation_stream_finish"),
  dictationId: z.string(),
  finalSeq: z.number().int().nonnegative(),
});

export const DictationStreamCancelMessageSchema = z.object({
  type: z.literal("dictation_stream_cancel"),
  dictationId: z.string(),
});

const GitSetupOptionsSchema = z.object({
  baseBranch: z.string().optional(),
  createNewBranch: z.boolean().optional(),
  newBranchName: z.string().optional(),
  createWorktree: z.boolean().optional(),
  worktreeSlug: z.string().optional(),
  refName: z.string().min(1).optional(),
  action: z.enum(["branch-off", "checkout"]).optional(),
  githubPrNumber: z.number().int().positive().optional(),
});

export type GitSetupOptions = z.infer<typeof GitSetupOptionsSchema>;

export const CreateAgentRequestMessageSchema = z.object({
  type: z.literal("create_agent_request"),
  config: AgentSessionConfigSchema,
  workspaceId: z.string().optional(),
  worktreeName: z.string().optional(),
  initialPrompt: z.string().optional(),
  clientMessageId: z.string().optional(),
  outputSchema: z.record(z.unknown()).optional(),
  images: z.array(ImageAttachmentSchema).optional(),
  files: z.array(FileAttachmentSchema).optional(),
  attachments: AgentAttachmentsSchema,
  git: GitSetupOptionsSchema.optional(),
  labels: z.record(z.string()).default({}),
  /**
   * @deprecated Phase 7 — ignored by daemon. Tab stacks live on auth-server.
   */
  stackId: z.string().nullable().optional(),
  requestId: z.string(),
});

export const ListProviderModelsRequestMessageSchema = z.object({
  type: z.literal("list_provider_models_request"),
  provider: AgentProviderSchema,
  cwd: z.string().optional(),
  requestId: z.string(),
});

export const ListProviderModesRequestMessageSchema = z.object({
  type: z.literal("list_provider_modes_request"),
  provider: AgentProviderSchema,
  cwd: z.string().optional(),
  requestId: z.string(),
});

export const ListAvailableProvidersRequestMessageSchema = z.object({
  type: z.literal("list_available_providers_request"),
  requestId: z.string(),
});

export const GetProvidersSnapshotRequestMessageSchema = z.object({
  type: z.literal("get_providers_snapshot_request"),
  cwd: z.string().optional(),
  requestId: z.string(),
});

export const RefreshProvidersSnapshotRequestMessageSchema = z.object({
  type: z.literal("refresh_providers_snapshot_request"),
  cwd: z.string().optional(),
  providers: z.array(AgentProviderSchema).optional(),
  requestId: z.string(),
});

export const ProviderDiagnosticRequestMessageSchema = z.object({
  type: z.literal("provider_diagnostic_request"),
  provider: AgentProviderSchema,
  requestId: z.string(),
});

export const ResumeAgentRequestMessageSchema = z.object({
  type: z.literal("resume_agent_request"),
  handle: AgentPersistenceHandleSchema,
  overrides: AgentSessionConfigSchema.partial().optional(),
  requestId: z.string(),
});

export const RefreshAgentRequestMessageSchema = z.object({
  type: z.literal("refresh_agent_request"),
  agentId: z.string(),
  requestId: z.string(),
});

export const CancelAgentRequestMessageSchema = z.object({
  type: z.literal("cancel_agent_request"),
  agentId: z.string(),
  requestId: z.string().optional(),
});

export const RestartServerRequestMessageSchema = z.object({
  type: z.literal("restart_server_request"),
  reason: z.string().optional(),
  requestId: z.string(),
});

export const ShutdownServerRequestMessageSchema = z.object({
  type: z.literal("shutdown_server_request"),
  requestId: z.string(),
});

export const AgentTimelineCursorSchema = z.object({
  epoch: z.string(),
  seq: z.number().int().nonnegative(),
});

export const FetchAgentTimelineRequestMessageSchema = z.object({
  type: z.literal("fetch_agent_timeline_request"),
  agentId: z.string(),
  requestId: z.string(),
  direction: z.enum(["tail", "before", "after"]).optional(),
  cursor: AgentTimelineCursorSchema.optional(),
  // 0 means "all matching rows for this query window".
  limit: z.number().int().nonnegative().optional(),
  // Default should be projected for app timeline loading.
  projection: z.enum(["projected", "canonical"]).optional(),
});

export const SetAgentModeRequestMessageSchema = z.object({
  type: z.literal("set_agent_mode_request"),
  agentId: z.string(),
  modeId: z.string(),
  requestId: z.string(),
});

export const SetAgentModeResponseMessageSchema = z.object({
  type: z.literal("set_agent_mode_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable(),
  }),
});

export const SetAgentModelRequestMessageSchema = z.object({
  type: z.literal("set_agent_model_request"),
  agentId: z.string(),
  modelId: z.string().nullable(),
  requestId: z.string(),
});

export const SetAgentModelResponseMessageSchema = z.object({
  type: z.literal("set_agent_model_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable(),
  }),
});

export const SetAgentThinkingRequestMessageSchema = z.object({
  type: z.literal("set_agent_thinking_request"),
  agentId: z.string(),
  thinkingOptionId: z.string().nullable(),
  requestId: z.string(),
});

export const SetAgentThinkingResponseMessageSchema = z.object({
  type: z.literal("set_agent_thinking_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable(),
  }),
});

export const SetAgentFeatureRequestMessageSchema = z.object({
  type: z.literal("set_agent_feature_request"),
  agentId: z.string(),
  featureId: z.string(),
  value: z.unknown(),
  requestId: z.string(),
});

export const SetAgentFeatureResponseMessageSchema = z.object({
  type: z.literal("set_agent_feature_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable(),
  }),
});

export const ListAgentMcpServersRequestMessageSchema = z.object({
  type: z.literal("list_agent_mcp_servers_request"),
  agentId: z.string(),
  requestId: z.string(),
});

export const ListAgentMcpServersResponseMessageSchema = z.object({
  type: z.literal("list_agent_mcp_servers_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    servers: z.array(z.object({ name: z.string(), status: z.string() })).nullable(),
    error: z.string().nullable(),
  }),
});

export const SetAgentMcpServerRequestMessageSchema = z.object({
  type: z.literal("set_agent_mcp_server_request"),
  agentId: z.string(),
  serverName: z.string(),
  enabled: z.boolean(),
  requestId: z.string(),
});

export const SetAgentMcpServerResponseMessageSchema = z.object({
  type: z.literal("set_agent_mcp_server_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable(),
  }),
});

export const UpdateAgentResponseMessageSchema = z.object({
  type: z.literal("update_agent_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable(),
  }),
});

export const SetVoiceModeResponseMessageSchema = z.object({
  type: z.literal("set_voice_mode_response"),
  payload: z.object({
    requestId: z.string(),
    enabled: z.boolean(),
    agentId: z.string().nullable(),
    accepted: z.boolean(),
    error: z.string().nullable(),
    reasonCode: z.string().optional(),
    retryable: z.boolean().optional(),
    missingModelIds: z.array(z.string()).optional(),
  }),
});

export const AgentPermissionResponseMessageSchema = z.object({
  type: z.literal("agent_permission_response"),
  agentId: z.string(),
  requestId: z.string(),
  response: AgentPermissionResponseSchema,
});

const CheckoutErrorCodeSchema = z.enum([
  "NOT_GIT_REPO",
  "NOT_ALLOWED",
  "MERGE_CONFLICT",
  "UNKNOWN",
]);

const CheckoutErrorSchema = z.object({
  code: CheckoutErrorCodeSchema,
  message: z.string(),
});

const CheckoutDiffCompareSchema = z.object({
  mode: z.enum(["uncommitted", "base"]),
  baseRef: z.string().optional(),
  ignoreWhitespace: z.boolean().optional(),
});

export const CheckoutStatusRequestSchema = z.object({
  type: z.literal("checkout_status_request"),
  cwd: z.string(),
  requestId: z.string(),
});

export const SubscribeCheckoutDiffRequestSchema = z.object({
  type: z.literal("subscribe_checkout_diff_request"),
  subscriptionId: z.string(),
  cwd: z.string(),
  compare: CheckoutDiffCompareSchema,
  requestId: z.string(),
});

export const UnsubscribeCheckoutDiffRequestSchema = z.object({
  type: z.literal("unsubscribe_checkout_diff_request"),
  subscriptionId: z.string(),
});

export const CheckoutCommitRequestSchema = z.object({
  type: z.literal("checkout_commit_request"),
  cwd: z.string(),
  message: z.string().optional(),
  addAll: z.boolean().optional(),
  requestId: z.string(),
});

export const CheckoutGenerateCommitMessageRequestSchema = z.object({
  type: z.literal("checkout_generate_commit_message_request"),
  cwd: z.string(),
  requestId: z.string(),
});

export const CheckoutMergeRequestSchema = z.object({
  type: z.literal("checkout_merge_request"),
  cwd: z.string(),
  baseRef: z.string().optional(),
  strategy: z.enum(["merge", "squash"]).optional(),
  requireCleanTarget: z.boolean().optional(),
  requestId: z.string(),
});

export const CheckoutMergeFromBaseRequestSchema = z.object({
  type: z.literal("checkout_merge_from_base_request"),
  cwd: z.string(),
  baseRef: z.string().optional(),
  requireCleanTarget: z.boolean().optional(),
  requestId: z.string(),
});

export const CheckoutPullRequestSchema = z.object({
  type: z.literal("checkout_pull_request"),
  cwd: z.string(),
  requestId: z.string(),
});

export const CheckoutPushRequestSchema = z.object({
  type: z.literal("checkout_push_request"),
  cwd: z.string(),
  requestId: z.string(),
});

export const CheckoutDeleteBranchRequestSchema = z.object({
  type: z.literal("checkout_delete_branch_request"),
  cwd: z.string(),
  branchName: z.string(),
  // Whether to also delete the matching ref on origin. UI typically passes
  // true; setting false leaves the remote alone (local-only cleanup).
  deleteRemote: z.boolean().optional(),
  requestId: z.string(),
});

export const CheckoutPrCreateRequestSchema = z.object({
  type: z.literal("checkout_pr_create_request"),
  cwd: z.string(),
  title: z.string().optional(),
  body: z.string().optional(),
  baseRef: z.string().optional(),
  requestId: z.string(),
});

export const CheckoutPrStatusRequestSchema = z.object({
  type: z.literal("checkout_pr_status_request"),
  cwd: z.string(),
  requestId: z.string(),
});

export const PullRequestTimelineRequestSchema = z.object({
  type: z.literal("pull_request_timeline_request"),
  cwd: z.string(),
  prNumber: z.number(),
  repoOwner: z.string(),
  repoName: z.string(),
  requestId: z.string(),
});

export const ValidateBranchRequestSchema = z.object({
  type: z.literal("validate_branch_request"),
  cwd: z.string(),
  branchName: z.string(),
  requestId: z.string(),
});

export const CheckoutSwitchBranchRequestSchema = z.object({
  type: z.literal("checkout_switch_branch_request"),
  cwd: z.string(),
  branch: z.string(),
  requestId: z.string(),
});

export const CheckoutCreateBranchRequestSchema = z.object({
  type: z.literal("checkout_create_branch_request"),
  cwd: z.string(),
  newBranchName: z.string(),
  baseBranch: z.string().optional(),
  requestId: z.string(),
});

export const StashSaveRequestSchema = z.object({
  type: z.literal("stash_save_request"),
  cwd: z.string(),
  /** Branch name to tag the stash with for later identification. */
  branch: z.string().optional(),
  requestId: z.string(),
});

export const StashPopRequestSchema = z.object({
  type: z.literal("stash_pop_request"),
  cwd: z.string(),
  /** Zero-based index from stash_list_response. */
  stashIndex: z.number().int().min(0),
  requestId: z.string(),
});

export const StashListRequestSchema = z.object({
  type: z.literal("stash_list_request"),
  cwd: z.string(),
  /** If true, only return appostle-created stashes. Default true. */
  appostleOnly: z.boolean().optional(),
  requestId: z.string(),
});

export const BranchSuggestionsRequestSchema = z.object({
  type: z.literal("branch_suggestions_request"),
  cwd: z.string(),
  query: z.string().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  requestId: z.string(),
});

export const GitHubSearchItemSchema = z.object({
  kind: z.enum(["issue", "pr"]),
  number: z.number(),
  title: z.string(),
  url: z.string(),
  state: z.string(),
  body: z.string().nullable(),
  labels: z.array(z.string()),
  baseRefName: z.string().nullable().optional(),
  headRefName: z.string().nullable().optional(),
  updatedAt: z.string().optional(),
});

export const GitHubSearchKindSchema = z.enum(["github-issue", "github-pr"]);

export const GitHubSearchRequestSchema = z.object({
  type: z.literal("github_search_request"),
  cwd: z.string(),
  query: z.string(),
  limit: z.number().int().min(1).max(50).optional(),
  kinds: z.array(GitHubSearchKindSchema).optional(),
  requestId: z.string(),
});

// GitLab search — parallel to the GitHub variants. Items use GitLab's `iid`
// rather than `number`, and the kind enum has `"mr"` instead of `"pr"`.
// Keeping the shapes parallel (rather than unifying behind one schema) avoids
// breaking the shipped GitHub schema and lets the wire stay obvious.
export const GitLabSearchItemSchema = z.object({
  kind: z.enum(["issue", "mr"]),
  iid: z.number(),
  title: z.string(),
  url: z.string(),
  state: z.string(),
  body: z.string().nullable(),
  labels: z.array(z.string()),
  sourceBranch: z.string().nullable().optional(),
  targetBranch: z.string().nullable().optional(),
  updatedAt: z.string().optional(),
});

export const GitLabSearchKindSchema = z.enum(["gitlab-issue", "gitlab-mr"]);

export const GitLabSearchRequestSchema = z.object({
  type: z.literal("gitlab_search_request"),
  cwd: z.string(),
  query: z.string(),
  limit: z.number().int().min(1).max(50).optional(),
  kinds: z.array(GitLabSearchKindSchema).optional(),
  requestId: z.string(),
});

// Provider-tagged "create issue" requests. Two separate message types (rather
// than one generic CreateIssueRequest) keep the routing and handler shape
// parallel to the search RPCs.
export const CreateGitHubIssueRequestSchema = z.object({
  type: z.literal("create_github_issue_request"),
  cwd: z.string(),
  title: z.string().min(1),
  body: z.string().optional(),
  labels: z.array(z.string()).optional(),
  requestId: z.string(),
});

export const CreateGitLabIssueRequestSchema = z.object({
  type: z.literal("create_gitlab_issue_request"),
  cwd: z.string(),
  title: z.string().min(1),
  body: z.string().optional(),
  labels: z.array(z.string()).optional(),
  requestId: z.string(),
});

// ────────────────────────────────────────────────────────────────────────────
// OAuth (PKCE) — currently GitLab only. The provider enum is single-value so
// adding GitHub later is additive. Request/response schemas live up here so
// they can be referenced by SessionInboundMessageSchema below.
// ────────────────────────────────────────────────────────────────────────────
export const OAuthProviderSchema = z.enum(["gitlab"]);

export const OAuthInitiateRequestSchema = z.object({
  type: z.literal("oauth_initiate_request"),
  provider: OAuthProviderSchema,
  requestId: z.string(),
});

export const OAuthInitiateResponseSchema = z.object({
  type: z.literal("oauth_initiate_response"),
  payload: z.object({
    authorizeUrl: z.string().nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const OAuthCompleteRequestSchema = z.object({
  type: z.literal("oauth_complete_request"),
  provider: OAuthProviderSchema,
  code: z.string().min(1),
  state: z.string().min(1),
  requestId: z.string(),
});

export const OAuthCompleteResponseSchema = z.object({
  type: z.literal("oauth_complete_response"),
  payload: z.object({
    username: z.string().nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const OAuthStatusRequestSchema = z.object({
  type: z.literal("oauth_status_request"),
  provider: OAuthProviderSchema,
  requestId: z.string(),
});

export const OAuthStatusResponseSchema = z.object({
  type: z.literal("oauth_status_response"),
  payload: z.object({
    signedIn: z.boolean(),
    username: z.string().nullable(),
    expiresAt: z.number().nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const OAuthSignOutRequestSchema = z.object({
  type: z.literal("oauth_sign_out_request"),
  provider: OAuthProviderSchema,
  requestId: z.string(),
});

export const OAuthSignOutResponseSchema = z.object({
  type: z.literal("oauth_sign_out_response"),
  payload: z.object({
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const DirectorySuggestionsRequestSchema = z.object({
  type: z.literal("directory_suggestions_request"),
  query: z.string(),
  cwd: z.string().optional(),
  includeFiles: z.boolean().optional(),
  includeDirectories: z.boolean().optional(),
  limit: z.number().int().min(1).max(100).optional(),
  requestId: z.string(),
});

/**
 * Browse-mode RPC for the project picker. Lists immediate children of an
 * arbitrary absolute filesystem path on the daemon machine. Unlike
 * `directory_suggestions_request` (scoped to HOME), this is unrestricted —
 * lets clients navigate the entire filesystem (USB drives, /Volumes, /mnt,
 * etc.) when adding a project. Hidden entries (dot-files) are filtered out
 * unless `includeHidden` is set.
 *
 * The empty string and "~" both resolve to the daemon's HOME for convenience.
 */
export const ListDirectoryRequestSchema = z.object({
  type: z.literal("list_directory_request"),
  path: z.string(),
  includeFiles: z.boolean().optional(),
  includeHidden: z.boolean().optional(),
  limit: z.number().int().min(1).max(2000).optional(),
  requestId: z.string(),
});

/**
 * List filesystem roots / drives the user might jump to when picking a project
 * directory. Cross-platform: macOS surfaces `/Volumes/*`, Linux surfaces
 * `/mnt/*` + `/media/<user>/*`, Windows enumerates drive letters. The home
 * directory is always included as a "home" kind entry.
 */
export const ListFilesystemRootsRequestSchema = z.object({
  type: z.literal("list_filesystem_roots_request"),
  requestId: z.string(),
});

export const AppostleWorktreeListRequestSchema = z.object({
  type: z.literal("appostle_worktree_list_request"),
  cwd: z.string().optional(),
  repoRoot: z.string().optional(),
  requestId: z.string(),
});

export const AppostleWorktreeArchiveRequestSchema = z.object({
  type: z.literal("appostle_worktree_archive_request"),
  worktreePath: z.string().optional(),
  repoRoot: z.string().optional(),
  branchName: z.string().optional(),
  requestId: z.string(),
});

export const CreateAppostleWorktreeRequestSchema = z.object({
  type: z.literal("create_appostle_worktree_request"),
  cwd: z.string(),
  worktreeSlug: z.string().optional(),
  attachments: AgentAttachmentsSchema,
  refName: z.string().min(1).optional(),
  action: z.enum(["branch-off", "checkout"]).optional(),
  githubPrNumber: z.number().int().positive().optional(),
  requestId: z.string(),
});

export const WorkspaceSetupStatusRequestSchema = z.object({
  type: z.literal("workspace_setup_status_request"),
  workspaceId: z.string(),
  requestId: z.string(),
});

// TODO(2026-07): Remove once most clients are on >=0.1.50 and support arbitrary editor ids.
export const LEGACY_EDITOR_TARGET_IDS = [
  "cursor",
  "vscode",
  "zed",
  "finder",
  "explorer",
  "file-manager",
] as const;

export const KNOWN_EDITOR_TARGET_IDS = [...LEGACY_EDITOR_TARGET_IDS, "webstorm"] as const;

export const KnownEditorTargetIdSchema = z.enum(KNOWN_EDITOR_TARGET_IDS);
export const LegacyEditorTargetIdSchema = z.enum(LEGACY_EDITOR_TARGET_IDS);
export const EditorTargetIdSchema = z.string().trim().min(1);

const KNOWN_EDITOR_TARGET_ID_SET = new Set<string>(KNOWN_EDITOR_TARGET_IDS);
const LEGACY_EDITOR_TARGET_ID_SET = new Set<string>(LEGACY_EDITOR_TARGET_IDS);

export function isKnownEditorTargetId(value: string): value is KnownEditorTargetId {
  return KNOWN_EDITOR_TARGET_ID_SET.has(value);
}

export function isLegacyEditorTargetId(value: string): value is LegacyEditorTargetId {
  return LEGACY_EDITOR_TARGET_ID_SET.has(value);
}

export const EditorTargetDescriptorPayloadSchema = z.object({
  id: EditorTargetIdSchema,
  label: z.string(),
});

export const ListAvailableEditorsRequestSchema = z.object({
  type: z.literal("list_available_editors_request"),
  requestId: z.string(),
});

export const OpenInEditorRequestSchema = z.object({
  type: z.literal("open_in_editor_request"),
  path: z.string(),
  editorId: EditorTargetIdSchema,
  requestId: z.string(),
});

export const OpenProjectRequestSchema = z.object({
  type: z.literal("open_project_request"),
  cwd: z.string(),
  requestId: z.string(),
});

export const ArchiveWorkspaceRequestSchema = z.object({
  type: z.literal("archive_workspace_request"),
  workspaceId: z.string(),
  requestId: z.string(),
});

export const InitRepoRequestSchema = z.object({
  type: z.literal("init_repo_request"),
  cwd: z.string(),
  requestId: z.string(),
});

export const CloneRepoRequestSchema = z.object({
  type: z.literal("clone_repo_request"),
  remoteUrl: z.string().min(1),
  destPath: z.string(),
  requestId: z.string(),
});

// Highlighted diff token schema
// Note: style can be a compound class name (e.g., "heading meta") from the syntax highlighter
const HighlightTokenSchema = z.object({
  text: z.string(),
  style: z.string().nullable(),
});

const DiffLineSchema = z.object({
  type: z.enum(["add", "remove", "context", "header"]),
  content: z.string(),
  tokens: z.array(HighlightTokenSchema).optional(),
});

const DiffHunkSchema = z.object({
  oldStart: z.number(),
  oldCount: z.number(),
  newStart: z.number(),
  newCount: z.number(),
  lines: z.array(DiffLineSchema),
});

const ParsedDiffFileSchema = z.object({
  path: z.string(),
  isNew: z.boolean(),
  isDeleted: z.boolean(),
  additions: z.number(),
  deletions: z.number(),
  hunks: z.array(DiffHunkSchema),
  status: z.enum(["ok", "too_large", "binary"]).optional(),
});

const FileExplorerEntrySchema = z.object({
  name: z.string(),
  path: z.string(),
  kind: z.enum(["file", "directory"]),
  size: z.number(),
  modifiedAt: z.string(),
});

const FileExplorerFileSchema = z.object({
  path: z.string(),
  kind: z.enum(["text", "image", "binary"]),
  encoding: z.enum(["utf-8", "base64", "none"]),
  content: z.string().optional(),
  mimeType: z.string().optional(),
  size: z.number(),
  modifiedAt: z.string(),
});

const FileExplorerDirectorySchema = z.object({
  path: z.string(),
  entries: z.array(FileExplorerEntrySchema),
});

export const FileExplorerRequestSchema = z.object({
  type: z.literal("file_explorer_request"),
  cwd: z.string(),
  path: z.string().optional(),
  mode: z.enum(["list", "file"]),
  requestId: z.string(),
});

export const WorkspaceSearchFilesRequestSchema = z.object({
  type: z.literal("workspace_search_files_request"),
  cwd: z.string(),
  query: z.string(),
  limit: z.number().int().positive().optional(),
  requestId: z.string(),
});

export const WorkspaceSearchFilesEntrySchema = z.object({
  path: z.string(),
  name: z.string(),
  kind: z.enum(["file", "directory"]),
});

export const ProjectIconRequestSchema = z.object({
  type: z.literal("project_icon_request"),
  cwd: z.string(),
  requestId: z.string(),
});

export const FileDownloadTokenRequestSchema = z.object({
  type: z.literal("file_download_token_request"),
  cwd: z.string(),
  path: z.string(),
  requestId: z.string(),
});

export const FileWriteRequestSchema = z.object({
  type: z.literal("file_write_request"),
  cwd: z.string(),
  path: z.string(),
  content: z.string(),
  requestId: z.string(),
});

export const FileDeleteRequestSchema = z.object({
  type: z.literal("file_delete_request"),
  cwd: z.string(),
  path: z.string(),
  requestId: z.string(),
});

export const FileExplorerDeleteRequestSchema = z.object({
  type: z.literal("file_explorer_delete_request"),
  cwd: z.string(),
  path: z.string(),
  requestId: z.string(),
});

export const FileMkdirRequestSchema = z.object({
  type: z.literal("file_mkdir_request"),
  cwd: z.string(),
  path: z.string(),
  requestId: z.string(),
});

export const FileMoveRequestSchema = z.object({
  type: z.literal("file_move_request"),
  cwd: z.string(),
  sourcePath: z.string(),
  destinationPath: z.string(),
  requestId: z.string(),
});

export const ClearAgentAttentionMessageSchema = z.object({
  type: z.literal("clear_agent_attention"),
  agentId: z.union([z.string(), z.array(z.string())]),
  requestId: z.string().optional(),
});

export const ClientHeartbeatMessageSchema = z.object({
  type: z.literal("client_heartbeat"),
  deviceType: z.enum(["web", "mobile"]),
  focusedAgentId: z.string().nullable(),
  lastActivityAt: z.string(),
  appVisible: z.boolean(),
  appVisibilityChangedAt: z.string().optional(),
});

export const PingMessageSchema = z.object({
  type: z.literal("ping"),
  requestId: z.string(),
  clientSentAt: z.number().int().optional(),
});

const ListCommandsDraftConfigSchema = z.object({
  provider: AgentProviderSchema,
  cwd: z.string(),
  modeId: z.string().optional(),
  model: z.string().optional(),
  thinkingOptionId: z.string().optional(),
  featureValues: z.record(z.unknown()).optional(),
});

export const ListProviderFeaturesRequestMessageSchema = z.object({
  type: z.literal("list_provider_features_request"),
  draftConfig: ListCommandsDraftConfigSchema,
  requestId: z.string(),
});

export const ListCommandsRequestSchema = z.object({
  type: z.literal("list_commands_request"),
  agentId: z.string(),
  draftConfig: ListCommandsDraftConfigSchema.optional(),
  requestId: z.string(),
});

// ============================================================================
// Skills (Plans-style modal — list/create/edit provider-native skills)
// ============================================================================

export const SkillProviderSchema = z.enum(["claude", "codex", "opencode"]);
export const SkillScopeSchema = z.enum(["project", "global", "codex-prompts"]);

export const SkillFrontmatterSchema = z.object({
  description: z.string().optional(),
  argumentHint: z.string().optional(),
  allowedTools: z.array(z.string()).optional(),
});

export const ListSkillsRequestSchema = z.object({
  type: z.literal("list_skills_request"),
  provider: SkillProviderSchema,
  scope: SkillScopeSchema,
  /** Required when scope === "project". Ignored otherwise. */
  workspaceRoot: z.string().optional(),
  requestId: z.string(),
});

export const CreateSkillRequestSchema = z.object({
  type: z.literal("create_skill_request"),
  provider: SkillProviderSchema,
  scope: SkillScopeSchema,
  workspaceRoot: z.string().optional(),
  name: z.string(),
  requestId: z.string(),
});

export const WriteSkillFrontmatterRequestSchema = z.object({
  type: z.literal("write_skill_frontmatter_request"),
  /** Absolute path inside an allowlisted skill root. */
  path: z.string(),
  /** Workspace root is passed so project-scoped skills can be authorized. */
  workspaceRoot: z.string().optional(),
  frontmatter: SkillFrontmatterSchema,
  requestId: z.string(),
});

export const RegisterPushTokenMessageSchema = z.object({
  type: z.literal("register_push_token"),
  token: z.string(),
  /** Stable per-install id; lets the daemon key prefs to a device, not a token. */
  deviceId: z.string().optional(),
  /** Per-event toggles. Missing entries fall back to the registry default. */
  preferences: NotificationPreferencesSchema.optional(),
});

export const UnregisterPushTokenMessageSchema = z.object({
  type: z.literal("unregister_push_token"),
  token: z.string(),
});

// Web Push (browser/PWA) — separate from native Expo tokens above.
export const WebPushSubscriptionSchema = z.object({
  endpoint: z.string(),
  keys: z.object({
    p256dh: z.string(),
    auth: z.string(),
  }),
});

export const RegisterWebPushSubscriptionMessageSchema = z.object({
  type: z.literal("register_web_push_subscription"),
  subscription: WebPushSubscriptionSchema,
  deviceId: z.string().optional(),
  preferences: NotificationPreferencesSchema.optional(),
});

export const UnregisterWebPushSubscriptionMessageSchema = z.object({
  type: z.literal("unregister_web_push_subscription"),
  endpoint: z.string(),
});

/**
 * Push a fresh preferences blob for an existing registration. The daemon looks
 * up the entry by `deviceId` (preferred) or by `token` / `endpoint` and
 * replaces its prefs in-place.
 */
export const UpdateNotificationPreferencesMessageSchema = z.object({
  type: z.literal("update_notification_preferences"),
  /** Stable per-install id — primary key for the lookup. */
  deviceId: z.string(),
  /** Native Expo token (when the device has a native registration). */
  token: z.string().optional(),
  /** Web Push endpoint (when the device has a web registration). */
  endpoint: z.string().optional(),
  preferences: NotificationPreferencesSchema,
});

export const GetVapidPublicKeyRequestSchema = z.object({
  type: z.literal("get_vapid_public_key_request"),
  requestId: z.string(),
});

export const GetVapidPublicKeyResponseSchema = z.object({
  type: z.literal("get_vapid_public_key_response"),
  requestId: z.string(),
  publicKey: z.string(),
});

export const SendTestWebPushRequestSchema = z.object({
  type: z.literal("send_test_web_push_request"),
  /** When set, only push to the entry matching this device id. */
  deviceId: z.string().optional(),
});

export const SendTestPushRequestSchema = z.object({
  type: z.literal("send_test_push_request"),
  /** When set, only push to the native entry matching this device id. */
  deviceId: z.string().optional(),
});

// ============================================================================
// Terminal Messages
// ============================================================================

export const ListTerminalsRequestSchema = z.object({
  type: z.literal("list_terminals_request"),
  cwd: z.string().optional(),
  requestId: z.string(),
});

export const SubscribeTerminalsRequestSchema = z.object({
  type: z.literal("subscribe_terminals_request"),
  cwd: z.string(),
});

export const UnsubscribeTerminalsRequestSchema = z.object({
  type: z.literal("unsubscribe_terminals_request"),
  cwd: z.string(),
});

export const CreateTerminalRequestSchema = z.object({
  type: z.literal("create_terminal_request"),
  cwd: z.string(),
  name: z.string().optional(),
  agentId: z.string().optional(),
  command: z.string().optional(),
  args: z.array(z.string()).optional(),
  requestId: z.string(),
});

export const StartWorkspaceScriptRequestSchema = z.object({
  type: z.literal("start_workspace_script_request"),
  workspaceId: z.string(),
  scriptName: z.string(),
  requestId: z.string(),
});

export const SubscribeTerminalRequestSchema = z.object({
  type: z.literal("subscribe_terminal_request"),
  terminalId: z.string(),
  requestId: z.string(),
});

export const UnsubscribeTerminalRequestSchema = z.object({
  type: z.literal("unsubscribe_terminal_request"),
  terminalId: z.string(),
});

const TerminalClientMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("input"), data: z.string() }),
  z.object({ type: z.literal("resize"), rows: z.number(), cols: z.number() }),
  z.object({
    type: z.literal("mouse"),
    row: z.number(),
    col: z.number(),
    button: z.number(),
    action: z.enum(["down", "up", "move"]),
  }),
]);

export const TerminalInputSchema = z.object({
  type: z.literal("terminal_input"),
  terminalId: z.string(),
  message: TerminalClientMessageSchema,
});

export const KillTerminalRequestSchema = z.object({
  type: z.literal("kill_terminal_request"),
  terminalId: z.string(),
  requestId: z.string(),
});

export const CaptureTerminalRequestSchema = z.object({
  type: z.literal("capture_terminal_request"),
  terminalId: z.string(),
  start: z.number().int().optional(),
  end: z.number().int().optional(),
  stripAnsi: z.boolean().default(true),
  requestId: z.string(),
});

// ============================================================================
// Account-bound pairing — daemon link RPC (regime B / Model 2)
// ============================================================================
// A signed-in app, immediately after a successful pair, hands the daemon a
// short-lived bootstrap token + the auth-server URL. The daemon redeems the
// token at POST {authServerUrl}/daemon/link, persists the resulting
// daemonToken/serverPubkey/userId/hostId, and starts polling the allow-list.
//
// Sent at most once per daemon (idempotency: daemon rejects re-link with
// `already_linked`). Best-effort from the app side — pairing succeeds even if
// linking fails (e.g. auth-server unreachable). See packages/auth-server.

export const LinkAccountRequestMessageSchema = z.object({
  type: z.literal("link_account_request"),
  requestId: z.string(),
  /** Bootstrap token from POST /devices/bootstrap-token. ~5 min TTL, single-use. */
  bootstrapToken: z.string().min(1),
  /** Origin of the auth-server (e.g. "https://auth.appostle.app"). */
  authServerUrl: z.string().min(1),
});

export const LinkAccountResponseMessageSchema = z.object({
  type: z.literal("link_account_response"),
  payload: z.discriminatedUnion("ok", [
    z.object({
      requestId: z.string(),
      ok: z.literal(true),
      hostId: z.string(),
      userId: z.string(),
    }),
    z.object({
      requestId: z.string(),
      ok: z.literal(false),
      error: z.string(),
    }),
  ]),
});

/**
 * Sent in response to ANY non-link WS message when the daemon is in
 * `awaiting_link` mode (no `account` block in `~/.appostle/config.json`).
 *
 * The web client should treat receipt of this as a hint to redirect the
 * user to the pairing flow (and to suppress the noisy generic errors that
 * a non-functional daemon would otherwise produce).
 *
 * `requestId` is echoed when present so the client can correlate; not all
 * inbound messages carry one, so it's optional.
 */
export const DaemonAwaitingLinkResponseMessageSchema = z.object({
  type: z.literal("daemon_awaiting_link_response"),
  payload: z.object({
    requestId: z.string().optional(),
    ok: z.literal(false),
    error: z.literal("daemon_awaiting_link"),
    /** The inbound message type that was rejected — for client logging. */
    rejectedType: z.string().optional(),
  }),
});

// ============================================================================
// Claude account token — per-user Claude subscription on shared hosts
// ============================================================================
//
// Each connecting user brings their own Claude account by pasting a long-lived
// OAuth token (`claude setup-token`, generated on their own machine). The daemon
// stores it keyed by the connection's *verified* userId and injects it as
// `CLAUDE_CODE_OAUTH_TOKEN` when that user's agents spawn. The wire never carries
// an identity — the daemon always uses the verified session owner.

/** App pastes the user's `claude setup-token` value for the daemon to store. */
export const ClaudeTokenSetRequestMessageSchema = z.object({
  type: z.literal("claude_token_set_request"),
  requestId: z.string(),
  token: z.string().min(1),
});

/** Daemon confirms the token was validated + stored (or why it wasn't). */
export const ClaudeTokenSetResponseMessageSchema = z.object({
  type: z.literal("claude_token_set_response"),
  requestId: z.string(),
  ok: z.boolean(),
  /** Whether the token actually authenticates (validated against Claude). */
  valid: z.boolean(),
  error: z.string().optional(),
});

/** App asks whether the connecting user has a stored, valid Claude token. */
export const ClaudeTokenStatusRequestMessageSchema = z.object({
  type: z.literal("claude_token_status_request"),
  requestId: z.string(),
});

export const ClaudeTokenStatusResponseMessageSchema = z.object({
  type: z.literal("claude_token_status_response"),
  requestId: z.string(),
  hasToken: z.boolean(),
  /** False when a stored token has expired / no longer authenticates. */
  valid: z.boolean(),
  /** ISO timestamp the token was added, when present. */
  addedAt: z.string().optional(),
});

/** App removes the connecting user's stored Claude token. */
export const ClaudeTokenRemoveRequestMessageSchema = z.object({
  type: z.literal("claude_token_remove_request"),
  requestId: z.string(),
});

export const ClaudeTokenRemoveResponseMessageSchema = z.object({
  type: z.literal("claude_token_remove_response"),
  requestId: z.string(),
  ok: z.boolean(),
  error: z.string().optional(),
});

// ============================================================================
// Session uploads — per-agent file attachments management
// ============================================================================
//
// These RPCs power the in-app "Files" tab inside an active session. They let
// the client list every file the user attached during the session and delete
// individual entries (which removes both the on-disk file and the manifest
// record). Adding new RPC schemas to the inbound/outbound unions is a purely
// additive change: old clients never send these and old daemons that don't
// recognize the type bucket will reply with the standard rpc_error.

export const ListSessionUploadsRequestSchema = z.object({
  type: z.literal("list_session_uploads_request"),
  requestId: z.string(),
  /** Accepts full ID, unique prefix, or exact full title (server resolves). */
  agentId: z.string(),
});

export const ListSessionUploadsResponseSchema = z.object({
  type: z.literal("list_session_uploads_response"),
  payload: z.discriminatedUnion("ok", [
    z.object({
      requestId: z.string(),
      ok: z.literal(true),
      uploads: z.array(SessionUploadSchema),
    }),
    z.object({
      requestId: z.string(),
      ok: z.literal(false),
      error: z.string(),
    }),
  ]),
});

export const DeleteSessionUploadRequestSchema = z.object({
  type: z.literal("delete_session_upload_request"),
  requestId: z.string(),
  /** Accepts full ID, unique prefix, or exact full title (server resolves). */
  agentId: z.string(),
  uploadId: z.string(),
});

export const DeleteSessionUploadResponseSchema = z.object({
  type: z.literal("delete_session_upload_response"),
  payload: z.discriminatedUnion("ok", [
    z.object({
      requestId: z.string(),
      ok: z.literal(true),
    }),
    z.object({
      requestId: z.string(),
      ok: z.literal(false),
      error: z.string(),
    }),
  ]),
});

// Public listing entry returned by list_session_images_response. Mirrors
// SessionUploadSchema in spirit but reflects the no-manifest reality of the
// image store: there is no `messageId` because we don't track it on disk —
// only the timeline echo carries that. `id` is the workspace-relative POSIX
// path used both as a stable React key and as the delete target.
export const SessionImageSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  size: z.number().int().nonnegative(),
  createdAt: z.string(), // ISO-8601 UTC timestamp
  relativePath: z.string(),
});

export const ListSessionImagesRequestSchema = z.object({
  type: z.literal("list_session_images_request"),
  requestId: z.string(),
  /** Accepts full ID, unique prefix, or exact full title (server resolves). */
  agentId: z.string(),
});

export const ListSessionImagesResponseSchema = z.object({
  type: z.literal("list_session_images_response"),
  payload: z.discriminatedUnion("ok", [
    z.object({
      requestId: z.string(),
      ok: z.literal(true),
      images: z.array(SessionImageSchema),
    }),
    z.object({
      requestId: z.string(),
      ok: z.literal(false),
      error: z.string(),
    }),
  ]),
});

export const DeleteSessionImageRequestSchema = z.object({
  type: z.literal("delete_session_image_request"),
  requestId: z.string(),
  /** Accepts full ID, unique prefix, or exact full title (server resolves). */
  agentId: z.string(),
  /** Workspace-relative POSIX path; traversal-guarded by the daemon. */
  relativePath: z.string(),
});

export const DeleteSessionImageResponseSchema = z.object({
  type: z.literal("delete_session_image_response"),
  payload: z.discriminatedUnion("ok", [
    z.object({
      requestId: z.string(),
      ok: z.literal(true),
    }),
    z.object({
      requestId: z.string(),
      ok: z.literal(false),
      error: z.string(),
    }),
  ]),
});

// ----- Fetch attachment bytes (Phase 4 of attachment-fetch-model migration) -----
//
// Lets any paired client (Electron after Cmd-R, phone, web) ask the daemon
// for image or file bytes by id. The daemon is the source of truth; clients
// use this to render thumbnails and previews without keeping local copies.
//
// `relativePath` is the workspace-relative POSIX path that lives on the
// `daemonRef` of the user_message timeline echo. Clients copy it through
// verbatim; the daemon traversal-guards it before reading.
//
// Adding new RPC schemas to the inbound/outbound unions is a purely additive
// change: old clients never send `fetch_attachment_bytes_request` and old
// daemons reply with the standard `rpc_error`.
export const FetchAttachmentBytesRequestSchema = z.object({
  type: z.literal("fetch_attachment_bytes_request"),
  requestId: z.string(),
  /** Accepts full ID, unique prefix, or exact full title (server resolves). */
  agentId: z.string(),
  kind: z.enum(["image", "file"]),
  attachmentId: z.string(),
  /** Workspace-relative POSIX path to the byte source on the daemon's disk. */
  relativePath: z.string(),
});

export const FetchAttachmentBytesResponseSchema = z.object({
  type: z.literal("fetch_attachment_bytes_response"),
  payload: z.discriminatedUnion("ok", [
    z.object({
      requestId: z.string(),
      ok: z.literal(true),
      attachmentId: z.string(),
      kind: z.enum(["image", "file"]),
      data: z.string(), // base64
      mimeType: z.string(),
      byteSize: z.number().int().nonnegative(),
      fileName: z.string().optional(),
    }),
    z.object({
      requestId: z.string(),
      ok: z.literal(false),
      error: z.string(),
    }),
  ]),
});

// ============================================================================
// Plan Usage (5h + weekly subscription limits via on-demand probe)
// ============================================================================

export const PlanUsageBucketSchema = z.object({
  rateLimitType: z.enum([
    "five_hour",
    "seven_day",
    "seven_day_opus",
    "seven_day_sonnet",
    "overage",
  ]),
  utilization: z.number(),
  resetsAt: z.number().optional(),
});

export const PlanUsageSnapshotSchema = z.object({
  provider: z.literal("claude"),
  buckets: z.array(PlanUsageBucketSchema),
  capturedAt: z.number(),
});

export const PlanUsageRequestSchema = z.object({
  type: z.literal("plan_usage_request"),
  requestId: z.string(),
  /** If true, force a fresh probe even if a cached snapshot exists. */
  refresh: z.boolean().optional(),
});

export type PlanUsageBucket = z.infer<typeof PlanUsageBucketSchema>;
export type PlanUsageSnapshot = z.infer<typeof PlanUsageSnapshotSchema>;

export const PlanUsageResponseSchema = z.object({
  type: z.literal("plan_usage_response"),
  payload: z.object({
    requestId: z.string(),
    snapshot: PlanUsageSnapshotSchema.nullable(),
    error: z.string().nullable(),
  }),
});

// ============================================================================
// Todo list — quick capture from any agent session (>todo …) into a per-
// project list rendered in the workspace top-bar dropdown. See
// `todo-store.ts` on the daemon and `todo-store.ts` (client) on the app.
// ============================================================================

export const SubtodoItemSchema = z.object({
  id: z.string(),
  todoId: z.string(),
  text: z.string(),
  position: z.number(),
  completedAt: z.string().nullable(),
  notes: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  scheduledAt: z.string().optional().nullable(),
  dueAt: z.string().optional().nullable(),
  createdByUserId: z.string().optional().nullable(),
  createdByUserName: z.string().optional().nullable(),
  createdByUserImage: z.string().optional().nullable(),
  createdByAgentId: z.string().optional().nullable(),
  createdByHostId: z.string().optional().nullable(),
  assignedToUserId: z.string().optional().nullable(),
  assignedToUserName: z.string().optional().nullable(),
  assignedToUserImage: z.string().optional().nullable(),
  /** v3 fields (migration 038) — parity with parent todos. */
  priority: z.number().optional().nullable(),
  scheduleTargetHostId: z.string().optional().nullable(),
  blockedBySubtodoId: z.string().optional().nullable(),
});

export type SubtodoItem = z.infer<typeof SubtodoItemSchema>;

export const TodoItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  notes: z.string(),
  cwd: z.string().nullable(),
  createdByAgentId: z.string().nullable(),
  createdAt: z.string(),
  /** ISO timestamp when the user marked this completed, or null. */
  completedAt: z.string().nullable(),
  /** Sort key within the bucket; lower = higher in the list. */
  position: z.number(),
  subtodos: z.array(SubtodoItemSchema),
  /** Daemon serverId of the host whose agent created this todo, or null. */
  linkedAgentHostId: z.string().nullable().optional(),
  /** Agent id that created this todo (for jump-to-agent badge), or null. */
  linkedAgentId: z.string().nullable().optional(),
  /** Creator provenance snapshot (auth-server migration 031). */
  createdByUserId: z.string().nullable().optional(),
  createdByUserName: z.string().nullable().optional(),
  createdByUserImage: z.string().nullable().optional(),
  /** Daemon serverId the todo was created from (auth-server migration 033). */
  createdByHostId: z.string().nullable().optional(),
  /** Assignment (auth-server migration 032). assigned-to account + display snapshot. */
  assignedToUserId: z.string().nullable().optional(),
  assignedToUserName: z.string().nullable().optional(),
  assignedToUserImage: z.string().nullable().optional(),
  /** v2 fields (migration 034) */
  status: z.string().optional().nullable(),
  priority: z.number().optional().nullable(),
  scheduledAt: z.string().optional().nullable(),
  dueAt: z.string().optional().nullable(),
  scheduleTargetHostId: z.string().optional().nullable(),
  blockedByTodoId: z.string().optional().nullable(),
  resultReport: z.string().optional().nullable(),
});

export type TodoItem = z.infer<typeof TodoItemSchema>;

export const TodoListSubscribeRequestSchema = z.object({
  type: z.literal("todo_list_subscribe"),
  workspaceId: z.string(),
  requestId: z.string(),
});

export const TodoListUnsubscribeRequestSchema = z.object({
  type: z.literal("todo_list_unsubscribe"),
  workspaceId: z.string(),
});

export const TodoRemoveRequestSchema = z.object({
  type: z.literal("todo_remove_request"),
  todoId: z.string(),
  requestId: z.string(),
});

export const TodoSetCompletedRequestSchema = z.object({
  type: z.literal("todo_set_completed_request"),
  todoId: z.string(),
  completed: z.boolean(),
  requestId: z.string(),
});

export const TodoReorderRequestSchema = z.object({
  type: z.literal("todo_reorder_request"),
  workspaceId: z.string(),
  orderedTodoIds: z.array(z.string()),
  requestId: z.string(),
});

export const TodoAddRequestSchema = z.object({
  type: z.literal("todo_add_request"),
  workspaceId: z.string(),
  text: z.string().min(1).max(500),
  requestId: z.string(),
});

export const TodoNotesUpdateRequestSchema = z.object({
  type: z.literal("todo_notes_update_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  notes: z.string(),
  requestId: z.string(),
});

export const TodoNotesUpdateResponseSchema = z.object({
  type: z.literal("todo_notes_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoLinkedAgentUpdateRequestSchema = z.object({
  type: z.literal("todo_linked_agent_update_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  linkedAgentHostId: z.string().nullable(),
  linkedAgentId: z.string().nullable(),
  requestId: z.string(),
});

export const TodoLinkedAgentUpdateResponseSchema = z.object({
  type: z.literal("todo_linked_agent_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoAssigneeUpdateRequestSchema = z.object({
  type: z.literal("todo_assignee_update_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  /** Account id to assign to, or null to clear the assignment. */
  assignedToUserId: z.string().nullable(),
  requestId: z.string(),
});

export const TodoAssigneeUpdateResponseSchema = z.object({
  type: z.literal("todo_assignee_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskAddRequestSchema = z.object({
  type: z.literal("todo_subtask_add_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  text: z.string().min(1).max(500),
  requestId: z.string(),
});

export const TodoSubtaskAddResponseSchema = z.object({
  type: z.literal("todo_subtask_add_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    subtodo: SubtodoItemSchema.optional(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoUpdateTextRequestSchema = z.object({
  type: z.literal("todo_update_text_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  text: z.string().min(1).max(500),
  requestId: z.string(),
});

export const TodoUpdateTextResponseSchema = z.object({
  type: z.literal("todo_update_text_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskUpdateTextRequestSchema = z.object({
  type: z.literal("todo_subtask_update_text_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  text: z.string().min(1).max(500),
  requestId: z.string(),
});

export const TodoSubtaskUpdateTextResponseSchema = z.object({
  type: z.literal("todo_subtask_update_text_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskRemoveRequestSchema = z.object({
  type: z.literal("todo_subtask_remove_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  requestId: z.string(),
});

export const TodoSubtaskRemoveResponseSchema = z.object({
  type: z.literal("todo_subtask_remove_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskSetCompletedRequestSchema = z.object({
  type: z.literal("todo_subtask_set_completed_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  completed: z.boolean(),
  requestId: z.string(),
});

export const TodoSubtaskSetCompletedResponseSchema = z.object({
  type: z.literal("todo_subtask_set_completed_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskReorderRequestSchema = z.object({
  type: z.literal("todo_subtask_reorder_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  orderedSubtodoIds: z.array(z.string()),
  requestId: z.string(),
});

export const TodoSubtaskReorderResponseSchema = z.object({
  type: z.literal("todo_subtask_reorder_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoActivityItemSchema = z.object({
  id: z.string(),
  todoId: z.string(),
  subtodoId: z.string().nullable(),
  kind: z.string(),
  actorUserId: z.string().nullable(),
  actorUserName: z.string().nullable(),
  actorUserImage: z.string().nullable(),
  actorAgentId: z.string().nullable(),
  actorHostId: z.string().nullable(),
  body: z.string().nullable(),
  createdAt: z.string(),
});
export type TodoActivityItem = z.infer<typeof TodoActivityItemSchema>;

export const TodoStatusUpdateRequestSchema = z.object({
  type: z.literal("todo_status_update_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  status: z.enum(["open", "in_progress", "blocked", "review", "done"]),
  requestId: z.string(),
});
export const TodoStatusUpdateResponseSchema = z.object({
  type: z.literal("todo_status_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoPriorityUpdateRequestSchema = z.object({
  type: z.literal("todo_priority_update_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  priority: z.number().int().min(0).max(4),
  requestId: z.string(),
});
export const TodoPriorityUpdateResponseSchema = z.object({
  type: z.literal("todo_priority_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoScheduleUpdateRequestSchema = z.object({
  type: z.literal("todo_schedule_update_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  scheduledAt: z.string().nullable().optional(),
  dueAt: z.string().nullable().optional(),
  scheduleTargetHostId: z.string().nullable().optional(),
  requestId: z.string(),
});
export const TodoScheduleUpdateResponseSchema = z.object({
  type: z.literal("todo_schedule_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoBlockedByUpdateRequestSchema = z.object({
  type: z.literal("todo_blocked_by_update_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  blockedByTodoId: z.string().nullable(),
  requestId: z.string(),
});
export const TodoBlockedByUpdateResponseSchema = z.object({
  type: z.literal("todo_blocked_by_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoResultReportUpdateRequestSchema = z.object({
  type: z.literal("todo_result_report_update_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  resultReport: z.string().nullable(),
  requestId: z.string(),
});
export const TodoResultReportUpdateResponseSchema = z.object({
  type: z.literal("todo_result_report_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoActivityAddRequestSchema = z.object({
  type: z.literal("todo_activity_add_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  body: z.string().min(1),
  requestId: z.string(),
});
export const TodoActivityAddResponseSchema = z.object({
  type: z.literal("todo_activity_add_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    activity: TodoActivityItemSchema.optional(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoActivityListRequestSchema = z.object({
  type: z.literal("todo_activity_list_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  requestId: z.string(),
});
export const TodoActivityListResponseSchema = z.object({
  type: z.literal("todo_activity_list_response"),
  payload: z.object({
    requestId: z.string(),
    activity: z.array(TodoActivityItemSchema),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSessionItemSchema = z.object({
  id: z.string(),
  todoId: z.string(),
  hostId: z.string(),
  agentId: z.string(),
  linkedAt: z.string(),
  unlinkedAt: z.string().nullable(),
});
export type TodoSessionItem = z.infer<typeof TodoSessionItemSchema>;

export const TodoSessionsListRequestSchema = z.object({
  type: z.literal("todo_sessions_list_request"),
  workspaceId: z.string(),
  todoId: z.string(),
  requestId: z.string(),
});
export const TodoSessionsListResponseSchema = z.object({
  type: z.literal("todo_sessions_list_response"),
  payload: z.object({
    requestId: z.string(),
    sessions: z.array(TodoSessionItemSchema),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskNotesUpdateRequestSchema = z.object({
  type: z.literal("todo_subtask_notes_update_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  notes: z.string(),
  requestId: z.string(),
});
export const TodoSubtaskNotesUpdateResponseSchema = z.object({
  type: z.literal("todo_subtask_notes_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskAssigneeUpdateRequestSchema = z.object({
  type: z.literal("todo_subtask_assignee_update_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  assignedToUserId: z.string().nullable(),
  requestId: z.string(),
});
export const TodoSubtaskAssigneeUpdateResponseSchema = z.object({
  type: z.literal("todo_subtask_assignee_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskStatusUpdateRequestSchema = z.object({
  type: z.literal("todo_subtask_status_update_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  status: z.enum(["open", "in_progress", "blocked", "review", "done"]),
  requestId: z.string(),
});
export const TodoSubtaskStatusUpdateResponseSchema = z.object({
  type: z.literal("todo_subtask_status_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskScheduleUpdateRequestSchema = z.object({
  type: z.literal("todo_subtask_schedule_update_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  scheduledAt: z.string().nullable().optional(),
  dueAt: z.string().nullable().optional(),
  scheduleTargetHostId: z.string().nullable().optional(),
  requestId: z.string(),
});
export const TodoSubtaskScheduleUpdateResponseSchema = z.object({
  type: z.literal("todo_subtask_schedule_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskPriorityUpdateRequestSchema = z.object({
  type: z.literal("todo_subtask_priority_update_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  priority: z.number().int().min(0).max(4),
  requestId: z.string(),
});
export const TodoSubtaskPriorityUpdateResponseSchema = z.object({
  type: z.literal("todo_subtask_priority_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSubtaskBlockedByUpdateRequestSchema = z.object({
  type: z.literal("todo_subtask_blocked_by_update_request"),
  workspaceId: z.string(),
  subtodoId: z.string(),
  blockedBySubtodoId: z.string().nullable(),
  requestId: z.string(),
});
export const TodoSubtaskBlockedByUpdateResponseSchema = z.object({
  type: z.literal("todo_subtask_blocked_by_update_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoListStateSchema = z.object({
  type: z.literal("todo_list_state"),
  payload: z.object({
    workspaceId: z.string(),
    repoKey: z.string().nullable(),
    noRemoteReason: z.enum(["no_remote"]).nullable(),
    todos: z.array(TodoItemSchema),
  }),
});

export const TodoRemoveResponseSchema = z.object({
  type: z.literal("todo_remove_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoSetCompletedResponseSchema = z.object({
  type: z.literal("todo_set_completed_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoReorderResponseSchema = z.object({
  type: z.literal("todo_reorder_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable().optional(),
  }),
});

export const TodoAddResponseSchema = z.object({
  type: z.literal("todo_add_response"),
  payload: z.object({
    requestId: z.string(),
    accepted: z.boolean(),
    todoId: z.string().nullable(),
    error: z.string().nullable().optional(),
  }),
});

export type TodoListStateMessage = z.infer<typeof TodoListStateSchema>;
export type TodoRemoveResponseMessage = z.infer<typeof TodoRemoveResponseSchema>;
export type TodoSetCompletedResponseMessage = z.infer<typeof TodoSetCompletedResponseSchema>;
export type TodoReorderResponseMessage = z.infer<typeof TodoReorderResponseSchema>;

export const SessionInboundMessageSchema = z.discriminatedUnion("type", [
  VoiceAudioChunkMessageSchema,
  AbortRequestMessageSchema,
  AudioPlayedMessageSchema,
  LinkAccountRequestMessageSchema,
  ClaudeTokenSetRequestMessageSchema,
  ClaudeTokenStatusRequestMessageSchema,
  ClaudeTokenRemoveRequestMessageSchema,
  FetchAgentsRequestMessageSchema,
  FetchWorkspacesRequestMessageSchema,
  FetchAgentRequestMessageSchema,
  DeleteAgentRequestMessageSchema,
  ArchiveAgentRequestMessageSchema,
  ForkAgentRequestMessageSchema,
  RewindAgentRequestMessageSchema,
  CloseItemsRequestMessageSchema,
  UpdateAgentRequestMessageSchema,
  StackCreateRequestMessageSchema,
  StackRenameRequestMessageSchema,
  StackDeleteRequestMessageSchema,
  StackReorderRequestMessageSchema,
  StackSetActiveRequestMessageSchema,
  SetVoiceModeMessageSchema,
  SendAgentMessageRequestSchema,
  WaitForFinishRequestSchema,
  GetDaemonConfigRequestMessageSchema,
  SetDaemonConfigRequestMessageSchema,
  DictationStreamStartMessageSchema,
  DictationStreamChunkMessageSchema,
  DictationStreamFinishMessageSchema,
  DictationStreamCancelMessageSchema,
  CreateAgentRequestMessageSchema,
  ListProviderModelsRequestMessageSchema,
  ListProviderModesRequestMessageSchema,
  ListProviderFeaturesRequestMessageSchema,
  ListAvailableProvidersRequestMessageSchema,
  GetProvidersSnapshotRequestMessageSchema,
  RefreshProvidersSnapshotRequestMessageSchema,
  ProviderDiagnosticRequestMessageSchema,
  ResumeAgentRequestMessageSchema,
  RefreshAgentRequestMessageSchema,
  CancelAgentRequestMessageSchema,
  ShutdownServerRequestMessageSchema,
  RestartServerRequestMessageSchema,
  FetchAgentTimelineRequestMessageSchema,
  SetAgentModeRequestMessageSchema,
  SetAgentModelRequestMessageSchema,
  SetAgentThinkingRequestMessageSchema,
  SetAgentFeatureRequestMessageSchema,
  ListAgentMcpServersRequestMessageSchema,
  SetAgentMcpServerRequestMessageSchema,
  AgentPermissionResponseMessageSchema,
  CheckoutStatusRequestSchema,
  SubscribeCheckoutDiffRequestSchema,
  UnsubscribeCheckoutDiffRequestSchema,
  CheckoutCommitRequestSchema,
  CheckoutGenerateCommitMessageRequestSchema,
  CheckoutMergeRequestSchema,
  CheckoutMergeFromBaseRequestSchema,
  CheckoutPullRequestSchema,
  CheckoutPushRequestSchema,
  CheckoutDeleteBranchRequestSchema,
  CheckoutPrCreateRequestSchema,
  CheckoutPrStatusRequestSchema,
  PullRequestTimelineRequestSchema,
  CheckoutSwitchBranchRequestSchema,
  CheckoutCreateBranchRequestSchema,
  StashSaveRequestSchema,
  StashPopRequestSchema,
  StashListRequestSchema,
  ValidateBranchRequestSchema,
  BranchSuggestionsRequestSchema,
  GitHubSearchRequestSchema,
  GitLabSearchRequestSchema,
  CreateGitHubIssueRequestSchema,
  CreateGitLabIssueRequestSchema,
  OAuthInitiateRequestSchema,
  OAuthCompleteRequestSchema,
  OAuthStatusRequestSchema,
  OAuthSignOutRequestSchema,
  DirectorySuggestionsRequestSchema,
  ListDirectoryRequestSchema,
  ListFilesystemRootsRequestSchema,
  AppostleWorktreeListRequestSchema,
  AppostleWorktreeArchiveRequestSchema,
  CreateAppostleWorktreeRequestSchema,
  WorkspaceSetupStatusRequestSchema,
  ListAvailableEditorsRequestSchema,
  OpenInEditorRequestSchema,
  OpenProjectRequestSchema,
  InitRepoRequestSchema,
  CloneRepoRequestSchema,
  ArchiveWorkspaceRequestSchema,
  FileExplorerRequestSchema,
  WorkspaceSearchFilesRequestSchema,
  FileWriteRequestSchema,
  FileDeleteRequestSchema,
  FileExplorerDeleteRequestSchema,
  FileMkdirRequestSchema,
  FileMoveRequestSchema,
  ProjectIconRequestSchema,
  FileDownloadTokenRequestSchema,
  ClearAgentAttentionMessageSchema,
  ClientHeartbeatMessageSchema,
  PingMessageSchema,
  ListCommandsRequestSchema,
  ListSkillsRequestSchema,
  CreateSkillRequestSchema,
  WriteSkillFrontmatterRequestSchema,
  RolesListScopeRequestSchema,
  RoleCreateRequestSchema,
  RoleWriteFrontmatterRequestSchema,
  RoleMoveRequestSchema,
  BrandsListScopeRequestSchema,
  BrandCreateRequestSchema,
  BrandWriteFrontmatterRequestSchema,
  BrandAssetCopyRequestSchema,
  BrandAssetUploadRequestSchema,
  BrandAssetDeleteRequestSchema,
  BrandAssetRenameRequestSchema,
  BrandGenerateTokensRequestSchema,
  BrandTokenGenerationStatusRequestSchema,
  BrandGenerateLayoutRequestSchema,
  BrandLayoutQaNextRequestSchema,
  BrandGenerateWireframeRequestSchema,
  BrandAnalyzePhotographyRequestSchema,
  RightFontLibraryRequestSchema,
  GoogleFontsCatalogRequestSchema,
  GoogleFontsDownloadRequestSchema,
  RegisterPushTokenMessageSchema,
  UnregisterPushTokenMessageSchema,
  RegisterWebPushSubscriptionMessageSchema,
  UnregisterWebPushSubscriptionMessageSchema,
  UpdateNotificationPreferencesMessageSchema,
  GetVapidPublicKeyRequestSchema,
  SendTestWebPushRequestSchema,
  SendTestPushRequestSchema,
  ListTerminalsRequestSchema,
  SubscribeTerminalsRequestSchema,
  UnsubscribeTerminalsRequestSchema,
  CreateTerminalRequestSchema,
  StartWorkspaceScriptRequestSchema,
  SubscribeTerminalRequestSchema,
  UnsubscribeTerminalRequestSchema,
  TerminalInputSchema,
  KillTerminalRequestSchema,
  CaptureTerminalRequestSchema,
  ChatCreateRequestSchema,
  ChatListRequestSchema,
  ChatInspectRequestSchema,
  ChatDeleteRequestSchema,
  ChatPostRequestSchema,
  ChatReadRequestSchema,
  ChatWaitRequestSchema,
  PreviewOpenRequestSchema,
  PreviewFrameRequestSchema,
  PreviewScrollRequestSchema,
  PreviewPickRequestSchema,
  PreviewCloseRequestSchema,
  PreviewInputRequestSchema,
  PreviewResizeRequestSchema,
  ScheduleCreateRequestSchema,
  ScheduleListRequestSchema,
  ScheduleInspectRequestSchema,
  ScheduleLogsRequestSchema,
  SchedulePauseRequestSchema,
  ScheduleResumeRequestSchema,
  ScheduleDeleteRequestSchema,
  LoopRunRequestSchema,
  LoopListRequestSchema,
  LoopInspectRequestSchema,
  LoopLogsRequestSchema,
  LoopStopRequestSchema,
  QuestRunRequestSchema,
  QuestListRequestSchema,
  QuestInspectRequestSchema,
  QuestStopRequestSchema,
  RolesListRequestSchema,
  ListSessionUploadsRequestSchema,
  DeleteSessionUploadRequestSchema,
  ListSessionImagesRequestSchema,
  DeleteSessionImageRequestSchema,
  FetchAttachmentBytesRequestSchema,
  PlanUsageRequestSchema,
  TodoListSubscribeRequestSchema,
  TodoListUnsubscribeRequestSchema,
  TodoRemoveRequestSchema,
  TodoSetCompletedRequestSchema,
  TodoReorderRequestSchema,
  TodoAddRequestSchema,
  TodoNotesUpdateRequestSchema,
  TodoLinkedAgentUpdateRequestSchema,
  TodoAssigneeUpdateRequestSchema,
  TodoUpdateTextRequestSchema,
  TodoSubtaskAddRequestSchema,
  TodoSubtaskUpdateTextRequestSchema,
  TodoSubtaskRemoveRequestSchema,
  TodoSubtaskSetCompletedRequestSchema,
  TodoSubtaskReorderRequestSchema,
  TodoStatusUpdateRequestSchema,
  TodoPriorityUpdateRequestSchema,
  TodoScheduleUpdateRequestSchema,
  TodoBlockedByUpdateRequestSchema,
  TodoResultReportUpdateRequestSchema,
  TodoActivityAddRequestSchema,
  TodoActivityListRequestSchema,
  TodoSessionsListRequestSchema,
  TodoSubtaskNotesUpdateRequestSchema,
  TodoSubtaskAssigneeUpdateRequestSchema,
  TodoSubtaskStatusUpdateRequestSchema,
  TodoSubtaskScheduleUpdateRequestSchema,
  TodoSubtaskPriorityUpdateRequestSchema,
  TodoSubtaskBlockedByUpdateRequestSchema,
  ShareSessionRequestMessageSchema,
  UnshareSessionRequestMessageSchema,
]);

export type SessionInboundMessage = z.infer<typeof SessionInboundMessageSchema>;

// ============================================================================
// Session Outbound Messages (Session emits these)
// ============================================================================

export const ActivityLogPayloadSchema = z.object({
  id: z.string(),
  timestamp: z.coerce.date(),
  type: z.enum(["transcript", "assistant", "tool_call", "tool_result", "error", "system"]),
  content: z.string(),
  metadata: z.record(z.unknown()).optional(),
});

export const ActivityLogMessageSchema = z.object({
  type: z.literal("activity_log"),
  payload: ActivityLogPayloadSchema,
});

export const AssistantChunkMessageSchema = z.object({
  type: z.literal("assistant_chunk"),
  payload: z.object({
    chunk: z.string(),
  }),
});

export const AudioOutputMessageSchema = z.object({
  type: z.literal("audio_output"),
  payload: z.object({
    audio: z.string(), // base64 encoded
    format: z.string(),
    id: z.string(),
    isVoiceMode: z.boolean(), // Mode when audio was generated (for drift protection)
    groupId: z.string().optional(), // Logical utterance id
    chunkIndex: z.number().int().nonnegative().optional(),
    isLastChunk: z.boolean().optional(),
  }),
});

export const TranscriptionResultMessageSchema = z.object({
  type: z.literal("transcription_result"),
  payload: z.object({
    text: z.string(),
    language: z.string().optional(),
    duration: z.number().optional(),
    requestId: z.string(), // Echoed back from request for tracking
    avgLogprob: z.number().optional(),
    isLowConfidence: z.boolean().optional(),
    byteLength: z.number().optional(),
    format: z.string().optional(),
    debugRecordingPath: z.string().optional(),
  }),
});

export const VoiceInputStateMessageSchema = z.object({
  type: z.literal("voice_input_state"),
  payload: z.object({
    isSpeaking: z.boolean(),
  }),
});

export const DictationStreamAckMessageSchema = z.object({
  type: z.literal("dictation_stream_ack"),
  payload: z.object({
    dictationId: z.string(),
    ackSeq: z.number().int(),
  }),
});

export const DictationStreamFinishAcceptedMessageSchema = z.object({
  type: z.literal("dictation_stream_finish_accepted"),
  payload: z.object({
    dictationId: z.string(),
    timeoutMs: z.number().int().positive(),
  }),
});

export const DictationStreamPartialMessageSchema = z.object({
  type: z.literal("dictation_stream_partial"),
  payload: z.object({
    dictationId: z.string(),
    text: z.string(),
  }),
});

export const DictationStreamFinalMessageSchema = z.object({
  type: z.literal("dictation_stream_final"),
  payload: z.object({
    dictationId: z.string(),
    text: z.string(),
    debugRecordingPath: z.string().optional(),
  }),
});

export const DictationStreamErrorMessageSchema = z.object({
  type: z.literal("dictation_stream_error"),
  payload: z.object({
    dictationId: z.string(),
    error: z.string(),
    retryable: z.boolean(),
    reasonCode: z.string().optional(),
    missingModelIds: z.array(z.string()).optional(),
    debugRecordingPath: z.string().optional(),
  }),
});

export const ServerCapabilityStateSchema = z.object({
  enabled: z.boolean(),
  reason: z.string(),
});

export const ServerVoiceCapabilitiesSchema = z.object({
  dictation: ServerCapabilityStateSchema,
  voice: ServerCapabilityStateSchema,
});

export const ServerCapabilitiesSchema = z
  .object({
    voice: ServerVoiceCapabilitiesSchema.optional(),
  })
  .passthrough();

const ServerInfoHostnameSchema = z.unknown().transform((value): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
});

const ServerInfoVersionSchema = z.unknown().transform((value): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
});

const ServerCapabilitiesFromUnknownSchema = z
  .unknown()
  .optional()
  .transform((value): z.infer<typeof ServerCapabilitiesSchema> | undefined => {
    if (value === undefined) {
      return undefined;
    }
    const parsed = ServerCapabilitiesSchema.safeParse(value);
    if (!parsed.success) {
      return undefined;
    }
    return parsed.data;
  });

export const ServerInfoStatusPayloadSchema = z
  .object({
    status: z.literal("server_info"),
    serverId: z.string().trim().min(1),
    hostname: ServerInfoHostnameSchema.optional(),
    version: ServerInfoVersionSchema.optional(),
    // Curated Lucide icon id set by the daemon operator. Optional — unknown
    // values are ignored by the client, which falls back to a generic icon.
    // Allow null on the wire: the server emits `daemonIcon: null` when unset
    // so the field is always present in the payload (matches the normalized
    // shape returned by the schema transform).
    daemonIcon: z.string().nullable().optional(),
    capabilities: ServerCapabilitiesFromUnknownSchema,
    // COMPAT(providersSnapshot): added in v0.1.48, remove gating when all clients use snapshot
    features: z
      .object({
        providersSnapshot: z.boolean().optional(),
      })
      .optional(),
  })
  .passthrough()
  .transform((payload) => ({
    ...payload,
    hostname: payload.hostname ?? null,
    version: payload.version ?? null,
    daemonIcon: payload.daemonIcon ?? null,
  }));

export const StatusMessageSchema = z.object({
  type: z.literal("status"),
  payload: z
    .object({
      status: z.string(),
    })
    .passthrough(), // Allow additional fields
});

export const PongMessageSchema = z.object({
  type: z.literal("pong"),
  payload: z.object({
    requestId: z.string(),
    clientSentAt: z.number().int().optional(),
    serverReceivedAt: z.number().int(),
    serverSentAt: z.number().int(),
  }),
});

export const RpcErrorMessageSchema = z.object({
  type: z.literal("rpc_error"),
  payload: z.object({
    requestId: z.string(),
    requestType: z.string().optional(),
    error: z.string(),
    code: z.string().optional(),
  }),
});

const AgentStatusWithRequestSchema = z.object({
  agentId: z.string(),
  requestId: z.string(),
});

const AgentStatusWithTimelineSchema = AgentStatusWithRequestSchema.extend({
  timelineSize: z.number().optional(),
});

export const AgentCreatedStatusPayloadSchema = z
  .object({
    status: z.literal("agent_created"),
    agent: AgentSnapshotPayloadSchema,
  })
  .extend(AgentStatusWithRequestSchema.shape);

export const AgentCreateFailedStatusPayloadSchema = z.object({
  status: z.literal("agent_create_failed"),
  requestId: z.string(),
  error: z.string(),
  errorCode: z.string().optional(),
});

export const AgentResumedStatusPayloadSchema = z
  .object({
    status: z.literal("agent_resumed"),
    agent: AgentSnapshotPayloadSchema,
  })
  .extend(AgentStatusWithTimelineSchema.shape);

export const AgentRefreshedStatusPayloadSchema = z
  .object({
    status: z.literal("agent_refreshed"),
  })
  .extend(AgentStatusWithTimelineSchema.shape);

export const RestartRequestedStatusPayloadSchema = z.object({
  status: z.literal("restart_requested"),
  clientId: z.string(),
  reason: z.string().optional(),
  requestId: z.string(),
});

export const ShutdownRequestedStatusPayloadSchema = z.object({
  status: z.literal("shutdown_requested"),
  clientId: z.string(),
  requestId: z.string(),
});

export const DaemonConfigChangedStatusPayloadSchema = z
  .object({
    status: z.literal("daemon_config_changed"),
    config: MutableDaemonConfigSchema,
  })
  .passthrough();

export const KnownStatusPayloadSchema = z.discriminatedUnion("status", [
  AgentCreatedStatusPayloadSchema,
  AgentCreateFailedStatusPayloadSchema,
  AgentResumedStatusPayloadSchema,
  AgentRefreshedStatusPayloadSchema,
  ShutdownRequestedStatusPayloadSchema,
  RestartRequestedStatusPayloadSchema,
  DaemonConfigChangedStatusPayloadSchema,
]);

export type KnownStatusPayload = z.infer<typeof KnownStatusPayloadSchema>;

export const ArtifactMessageSchema = z.object({
  type: z.literal("artifact"),
  payload: z.object({
    type: z.enum(["markdown", "diff", "image", "code"]),
    id: z.string(),
    title: z.string(),
    content: z.string(),
    isBase64: z.boolean(),
  }),
});

export const ProjectCheckoutLiteNotGitPayloadSchema = z
  .object({
    cwd: z.string(),
    isGit: z.literal(false),
    currentBranch: z.null(),
    remoteUrl: z.null(),
    worktreeRoot: z.null().optional(),
    isAppostleOwnedWorktree: z.literal(false),
    mainRepoRoot: z.null(),
  })
  .transform((value) => ({
    ...value,
    worktreeRoot: null,
  }));

export const ProjectCheckoutLiteGitNonAppostlePayloadSchema = z
  .object({
    cwd: z.string(),
    isGit: z.literal(true),
    currentBranch: z.string().nullable(),
    remoteUrl: z.string().nullable(),
    worktreeRoot: z.string().optional(),
    isAppostleOwnedWorktree: z.literal(false),
    mainRepoRoot: z.null(),
  })
  .transform((value) => ({
    ...value,
    worktreeRoot: value.worktreeRoot ?? value.cwd,
  }));

export const ProjectCheckoutLiteGitAppostlePayloadSchema = z
  .object({
    cwd: z.string(),
    isGit: z.literal(true),
    currentBranch: z.string().nullable(),
    remoteUrl: z.string().nullable(),
    worktreeRoot: z.string().optional(),
    isAppostleOwnedWorktree: z.literal(true),
    mainRepoRoot: z.string(),
  })
  .transform((value) => ({
    ...value,
    worktreeRoot: value.worktreeRoot ?? value.cwd,
  }));

export const ProjectCheckoutLitePayloadSchema = z.union([
  ProjectCheckoutLiteNotGitPayloadSchema,
  ProjectCheckoutLiteGitNonAppostlePayloadSchema,
  ProjectCheckoutLiteGitAppostlePayloadSchema,
]);

export const ProjectPlacementPayloadSchema = z.object({
  projectKey: z.string(),
  projectName: z.string(),
  checkout: ProjectCheckoutLitePayloadSchema,
});

export const WorkspaceScriptLifecycleSchema = z.enum(["running", "stopped"]);
export const WorkspaceScriptHealthSchema = z.enum(["healthy", "unhealthy"]);

export const WorkspaceScriptPayloadSchema = z.object({
  scriptName: z.string(),
  type: z.enum(["script", "service"]).optional().default("service"),
  hostname: z.string(),
  port: z.number().int().positive().nullable(),
  proxyUrl: z.string().nullable().optional().default(null),
  lifecycle: WorkspaceScriptLifecycleSchema,
  health: WorkspaceScriptHealthSchema.nullable(),
  exitCode: z.number().nullable().optional().default(null),
});

const WorkspaceGitRuntimePayloadSchema = z
  .object({
    currentBranch: z.string().nullable().optional(),
    remoteUrl: z.string().nullable().optional(),
    isAppostleOwnedWorktree: z.boolean().optional(),
    isDirty: z.boolean().nullable().optional(),
    aheadBehind: z
      .object({
        ahead: z.number(),
        behind: z.number(),
      })
      .nullable()
      .optional(),
    aheadOfOrigin: z.number().nullable().optional(),
    behindOfOrigin: z.number().nullable().optional(),
  })
  .optional()
  .nullable();

const WorkspaceGitHubRuntimePayloadSchema = z
  .object({
    featuresEnabled: z.boolean().optional(),
    pullRequest: z
      .object({
        url: z.string(),
        title: z.string(),
        state: z.string(),
        baseRefName: z.string(),
        headRefName: z.string(),
        isMerged: z.boolean(),
      })
      .nullable()
      .optional(),
    error: z
      .object({
        message: z.string(),
      })
      .nullable()
      .optional(),
    refreshedAt: z.string().nullable().optional(),
  })
  .optional()
  .nullable();

export const WorkspaceDescriptorPayloadSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  projectDisplayName: z.string(),
  projectRootPath: z.string(),
  workspaceDirectory: z.string(),
  projectKind: z.enum(["git", "non_git", "directory"]),
  // COMPAT(workspaces): keep legacy directory workspace kind parseable.
  workspaceKind: z.enum(["directory", "local_checkout", "checkout", "worktree"]),
  name: z.string(),
  status: WorkspaceStateBucketSchema,
  activityAt: z.string().nullable(),
  // Branch-level total: all work on this branch since it diverged from the
  // base branch (commits + uncommitted). Shown in workspace-switcher rows and
  // other "comparing across branches" surfaces.
  branchDiffStat: z
    .object({
      additions: z.number(),
      deletions: z.number(),
    })
    .nullable()
    .optional(),
  // Working-tree: uncommitted changes only (HEAD -> worktree). Shown on the
  // toggle-explorer button and any "about to act in this workspace" surface so
  // the number always matches what the explorer panel shows.
  uncommittedDiffStat: z
    .object({
      additions: z.number(),
      deletions: z.number(),
    })
    .nullable()
    .optional(),
  scripts: z.array(WorkspaceScriptPayloadSchema).default([]),
  gitRuntime: WorkspaceGitRuntimePayloadSchema,
  githubRuntime: WorkspaceGitHubRuntimePayloadSchema,
});

export const AgentUpdateMessageSchema = z.object({
  type: z.literal("agent_update"),
  payload: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("upsert"),
      agent: AgentSnapshotPayloadSchema,
      project: ProjectPlacementPayloadSchema,
    }),
    z.object({
      kind: z.literal("remove"),
      agentId: z.string(),
    }),
  ]),
});

export const AgentStreamMessageSchema = z.object({
  type: z.literal("agent_stream"),
  payload: z.object({
    agentId: z.string(),
    event: AgentStreamEventPayloadSchema,
    timestamp: z.string(),
    // Present for timeline events. Maps 1:1 to canonical in-memory timeline rows.
    seq: z.number().int().nonnegative().optional(),
    epoch: z.string().optional(),
  }),
});

export const AgentStatusMessageSchema = z.object({
  type: z.literal("agent_status"),
  payload: z.object({
    agentId: z.string(),
    status: z.string(),
    info: AgentSnapshotPayloadSchema,
  }),
});

export const AgentListMessageSchema = z.object({
  type: z.literal("agent_list"),
  payload: z.object({
    agents: z.array(AgentSnapshotPayloadSchema),
  }),
});

export const FetchAgentsResponseMessageSchema = z.object({
  type: z.literal("fetch_agents_response"),
  payload: z.object({
    requestId: z.string(),
    subscriptionId: z.string().nullable().optional(),
    entries: z.array(
      z.object({
        agent: AgentSnapshotPayloadSchema,
        project: ProjectPlacementPayloadSchema,
      }),
    ),
    pageInfo: z.object({
      nextCursor: z.string().nullable(),
      prevCursor: z.string().nullable(),
      hasMore: z.boolean(),
    }),
  }),
});

/**
 * Daemon-pushed, unsolicited. Carries the first page of agents right after
 * `server_info` so the client can paint attention state before its own
 * paginated `fetch_agents_request` round-trip lands. Separate message type
 * (rather than reusing `fetch_agents_response`) because no client request
 * preceded it — there is no requestId to correlate against.
 */
export const InitialAgentSnapshotMessageSchema = z.object({
  type: z.literal("initial_agent_snapshot"),
  payload: z.object({
    entries: z.array(
      z.object({
        agent: AgentSnapshotPayloadSchema,
        project: ProjectPlacementPayloadSchema,
      }),
    ),
  }),
});

export const FetchWorkspacesResponseMessageSchema = z.object({
  type: z.literal("fetch_workspaces_response"),
  payload: z.object({
    requestId: z.string(),
    subscriptionId: z.string().nullable().optional(),
    entries: z.array(WorkspaceDescriptorPayloadSchema),
    pageInfo: z.object({
      nextCursor: z.string().nullable(),
      prevCursor: z.string().nullable(),
      hasMore: z.boolean(),
    }),
  }),
});

export const WorkspaceUpdateMessageSchema = z.object({
  type: z.literal("workspace_update"),
  payload: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("upsert"),
      workspace: WorkspaceDescriptorPayloadSchema,
    }),
    z.object({
      kind: z.literal("remove"),
      id: z.string(),
    }),
  ]),
});

export const ScriptStatusUpdateMessageSchema = z.object({
  type: z.literal("script_status_update"),
  payload: z.object({
    workspaceId: z.string(),
    scripts: z.array(WorkspaceScriptPayloadSchema),
  }),
});

export const WorkspaceSetupProgressMessageSchema = z.object({
  type: z.literal("workspace_setup_progress"),
  payload: z.object({
    workspaceId: z.string(),
    status: z.enum(["running", "completed", "failed"]),
    detail: WorktreeSetupDetailPayloadSchema,
    error: z.string().nullable(),
  }),
});

export const WorkspaceSetupSnapshotSchema = z.object({
  status: z.enum(["running", "completed", "failed"]),
  detail: WorktreeSetupDetailPayloadSchema,
  error: z.string().nullable(),
});

export const WorkspaceSetupStatusResponseMessageSchema = z.object({
  type: z.literal("workspace_setup_status_response"),
  payload: z.object({
    requestId: z.string(),
    workspaceId: z.string(),
    snapshot: WorkspaceSetupSnapshotSchema.nullable(),
  }),
});

export const OpenProjectResponseMessageSchema = z.object({
  type: z.literal("open_project_response"),
  payload: z.object({
    requestId: z.string(),
    workspace: WorkspaceDescriptorPayloadSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const InitRepoResponseMessageSchema = z.object({
  type: z.literal("init_repo_response"),
  payload: z.object({
    requestId: z.string(),
    workspace: WorkspaceDescriptorPayloadSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const CloneRepoResponseMessageSchema = z.object({
  type: z.literal("clone_repo_response"),
  payload: z.object({
    requestId: z.string(),
    workspace: WorkspaceDescriptorPayloadSchema.nullable(),
    error: z.string().nullable(),
  }),
});

export const StartWorkspaceScriptResponseMessageSchema = z.object({
  type: z.literal("start_workspace_script_response"),
  payload: z.object({
    requestId: z.string(),
    workspaceId: z.string(),
    scriptName: z.string(),
    terminalId: z.string().nullable(),
    error: z.string().nullable(),
  }),
});

export const ListAvailableEditorsResponseMessageSchema = z.object({
  type: z.literal("list_available_editors_response"),
  payload: z.object({
    requestId: z.string(),
    editors: z.array(EditorTargetDescriptorPayloadSchema),
    error: z.string().nullable(),
  }),
});

export const OpenInEditorResponseMessageSchema = z.object({
  type: z.literal("open_in_editor_response"),
  payload: z.object({
    requestId: z.string(),
    error: z.string().nullable(),
  }),
});

export const ArchiveWorkspaceResponseMessageSchema = z.object({
  type: z.literal("archive_workspace_response"),
  payload: z.object({
    requestId: z.string(),
    workspaceId: z.string(),
    archivedAt: z.string().nullable(),
    error: z.string().nullable(),
  }),
});

export const FetchAgentResponseMessageSchema = z.object({
  type: z.literal("fetch_agent_response"),
  payload: z.object({
    requestId: z.string(),
    agent: AgentSnapshotPayloadSchema.nullable(),
    project: ProjectPlacementPayloadSchema.nullable().optional(),
    error: z.string().nullable(),
  }),
});

const AgentTimelineSeqRangeSchema = z.object({
  startSeq: z.number().int().nonnegative(),
  endSeq: z.number().int().nonnegative(),
});

export const AgentTimelineEntryPayloadSchema = z.object({
  provider: AgentProviderSchema,
  item: AgentTimelineItemPayloadSchema,
  timestamp: z.string(),
  seqStart: z.number().int().nonnegative(),
  seqEnd: z.number().int().nonnegative(),
  sourceSeqRanges: z.array(AgentTimelineSeqRangeSchema),
  collapsed: z.array(z.enum(["assistant_merge", "tool_lifecycle"])),
});

export const FetchAgentTimelineResponseMessageSchema = z.object({
  type: z.literal("fetch_agent_timeline_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    agent: AgentSnapshotPayloadSchema.nullable(),
    direction: z.enum(["tail", "before", "after"]),
    projection: z.enum(["projected", "canonical"]),
    epoch: z.string(),
    reset: z.boolean(),
    staleCursor: z.boolean(),
    gap: z.boolean(),
    window: z.object({
      minSeq: z.number().int().nonnegative(),
      maxSeq: z.number().int().nonnegative(),
      nextSeq: z.number().int().nonnegative(),
    }),
    startCursor: AgentTimelineCursorSchema.nullable(),
    endCursor: AgentTimelineCursorSchema.nullable(),
    hasOlder: z.boolean(),
    hasNewer: z.boolean(),
    entries: z.array(AgentTimelineEntryPayloadSchema),
    error: z.string().nullable(),
  }),
});

export const CancelAgentResponseMessageSchema = z.object({
  type: z.literal("cancel_agent_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    agent: AgentSnapshotPayloadSchema.nullable(),
  }),
});

export const ClearAgentAttentionResponseMessageSchema = z.object({
  type: z.literal("clear_agent_attention_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string().or(z.array(z.string())),
    agents: z.array(AgentSnapshotPayloadSchema),
  }),
});

export const SendAgentMessageResponseMessageSchema = z.object({
  type: z.literal("send_agent_message_response"),
  payload: z.object({
    requestId: z.string(),
    agentId: z.string(),
    accepted: z.boolean(),
    error: z.string().nullable(),
  }),
});

export const WaitForFinishResponseMessageSchema = z.object({
  type: z.literal("wait_for_finish_response"),
  payload: z.object({
    requestId: z.string(),
    status: z.enum(["idle", "error", "permission", "timeout"]),
    final: AgentSnapshotPayloadSchema.nullable(),
    error: z.string().nullable(),
    lastMessage: z.string().nullable(),
  }),
});

export const GetDaemonConfigResponseMessageSchema = z.object({
  type: z.literal("get_daemon_config_response"),
  payload: z
    .object({
      requestId: z.string(),
      config: MutableDaemonConfigSchema,
    })
    .passthrough(),
});

export const SetDaemonConfigResponseMessageSchema = z.object({
  type: z.literal("set_daemon_config_response"),
  payload: z
    .object({
      requestId: z.string(),
      config: MutableDaemonConfigSchema,
    })
    .passthrough(),
});

export const AgentPermissionRequestMessageSchema = z.object({
  type: z.literal("agent_permission_request"),
  payload: z.object({
    agentId: z.string(),
    request: AgentPermissionRequestPayloadSchema,
  }),
});

export const AgentPermissionResolvedMessageSchema = z.object({
  type: z.literal("agent_permission_resolved"),
  payload: z.object({
    agentId: z.string(),
    requestId: z.string(),
    resolution: AgentPermissionResponseSchema,
  }),
});

export const AgentDeletedMessageSchema = z.object({
  type: z.literal("agent_deleted"),
  payload: z.object({
    agentId: z.string(),
    requestId: z.string(),
  }),
});

export const AgentArchivedMessageSchema = z.object({
  type: z.literal("agent_archived"),
  payload: z.object({
    agentId: z.string(),
    archivedAt: z.string(),
    requestId: z.string(),
  }),
});

export const ForkAgentResponseMessageSchema = z.object({
  type: z.literal("fork_agent_response"),
  payload: z.object({
    requestId: z.string(),
    success: z.boolean(),
    newAgentId: z.string().nullable(),
    parentAgentId: z.string(),
    forkedFromMessageUuid: z.string(),
    // Text of the user message that was dropped at the fork point. The
    // client uses this to prefill the new agent's composer so the user can
    // edit and re-send the message. Optional — older daemons that predate
    // this field will simply omit it; clients should fall back to whatever
    // local copy of the bubble text they already have.
    droppedUserText: z.string().nullable().optional(),
    // Full snapshot of the newly-forked agent. The client seeds its session
    // store with this BEFORE opening the workspace tab, so the tab lands in
    // the parent's stack instead of falling back to the Default stack while
    // it waits for the next agent_state broadcast. Optional → old daemons
    // that predate this field simply omit it; old clients ignore it.
    agent: AgentSnapshotPayloadSchema.optional(),
    error: z.string().nullable(),
  }),
});

export const RewindAgentResponseMessageSchema = z.object({
  type: z.literal("rewind_agent_response"),
  payload: z.object({
    requestId: z.string(),
    success: z.boolean(),
    agentId: z.string(),
    rewoundToMessageUuid: z.string(),
    // Text of the user message that was dropped at the rewind point. Like
    // fork's droppedUserText, the client uses it to prefill the composer
    // for re-edit. Optional for back-compat with daemons that predate this.
    droppedUserText: z.string().nullable().optional(),
    // Number of timeline rows that were dropped from the conversation. Useful
    // for diagnostics and for the client to know whether a meaningful change
    // happened. Optional for back-compat.
    droppedRowCount: z.number().int().nonnegative().optional(),
    error: z.string().nullable(),
  }),
});

const CloseItemsAgentResultSchema = z.object({
  agentId: z.string(),
  archivedAt: z.string(),
});

const CloseItemsTerminalResultSchema = z.object({
  terminalId: z.string(),
  success: z.boolean(),
});

export const CloseItemsResponseSchema = z.object({
  type: z.literal("close_items_response"),
  payload: z.object({
    agents: z.array(CloseItemsAgentResultSchema),
    terminals: z.array(CloseItemsTerminalResultSchema),
    requestId: z.string(),
  }),
});

const AheadBehindSchema = z.object({
  ahead: z.number(),
  behind: z.number(),
});

const CheckoutStatusCommonSchema = z.object({
  cwd: z.string(),
  error: CheckoutErrorSchema.nullable(),
  requestId: z.string(),
});

const CheckoutStatusNotGitSchema = CheckoutStatusCommonSchema.extend({
  isGit: z.literal(false),
  isAppostleOwnedWorktree: z.literal(false),
  repoRoot: z.null(),
  currentBranch: z.null(),
  isDirty: z.null(),
  baseRef: z.null(),
  aheadBehind: z.null(),
  aheadOfOrigin: z.null(),
  behindOfOrigin: z.null(),
  hasRemote: z.boolean(),
  remoteUrl: z.null(),
});

const CheckoutStatusGitNonAppostleSchema = CheckoutStatusCommonSchema.extend({
  isGit: z.literal(true),
  isAppostleOwnedWorktree: z.literal(false),
  repoRoot: z.string(),
  currentBranch: z.string().nullable(),
  isDirty: z.boolean(),
  baseRef: z.string().nullable(),
  aheadBehind: AheadBehindSchema.nullable(),
  aheadOfOrigin: z.number().nullable(),
  behindOfOrigin: z.number().nullable(),
  hasRemote: z.boolean(),
  remoteUrl: z.string().nullable(),
});

const CheckoutStatusGitAppostleSchema = CheckoutStatusCommonSchema.extend({
  isGit: z.literal(true),
  isAppostleOwnedWorktree: z.literal(true),
  repoRoot: z.string(),
  mainRepoRoot: z.string(),
  currentBranch: z.string().nullable(),
  isDirty: z.boolean(),
  baseRef: z.string(),
  aheadBehind: AheadBehindSchema.nullable(),
  aheadOfOrigin: z.number().nullable(),
  behindOfOrigin: z.number().nullable(),
  hasRemote: z.boolean(),
  remoteUrl: z.string().nullable(),
});

export const CheckoutStatusResponseSchema = z.object({
  type: z.literal("checkout_status_response"),
  payload: z.union([
    CheckoutStatusNotGitSchema,
    CheckoutStatusGitNonAppostleSchema,
    CheckoutStatusGitAppostleSchema,
  ]),
});

const CheckoutDiffSubscriptionPayloadSchema = z.object({
  subscriptionId: z.string(),
  cwd: z.string(),
  files: z.array(ParsedDiffFileSchema),
  error: CheckoutErrorSchema.nullable(),
});

export const SubscribeCheckoutDiffResponseSchema = z.object({
  type: z.literal("subscribe_checkout_diff_response"),
  payload: CheckoutDiffSubscriptionPayloadSchema.extend({
    requestId: z.string(),
  }),
});

export const CheckoutDiffUpdateSchema = z.object({
  type: z.literal("checkout_diff_update"),
  payload: CheckoutDiffSubscriptionPayloadSchema,
});

export const CheckoutCommitResponseSchema = z.object({
  type: z.literal("checkout_commit_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutGenerateCommitMessageResponseSchema = z.object({
  type: z.literal("checkout_generate_commit_message_response"),
  payload: z.object({
    cwd: z.string(),
    message: z.string().nullable(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutMergeResponseSchema = z.object({
  type: z.literal("checkout_merge_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutMergeFromBaseResponseSchema = z.object({
  type: z.literal("checkout_merge_from_base_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutPullResponseSchema = z.object({
  type: z.literal("checkout_pull_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutPushResponseSchema = z.object({
  type: z.literal("checkout_push_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutDeleteBranchResponseSchema = z.object({
  type: z.literal("checkout_delete_branch_response"),
  payload: z.object({
    cwd: z.string(),
    branchName: z.string(),
    localDeleted: z.boolean(),
    remoteDeleted: z.boolean(),
    // Set when the remote-side delete failed even though the local one
    // succeeded — the UI surfaces this as a non-fatal warning toast.
    remoteError: z.string().nullable().optional(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutPrCreateResponseSchema = z.object({
  type: z.literal("checkout_pr_create_response"),
  payload: z.object({
    cwd: z.string(),
    url: z.string().nullable(),
    number: z.number().nullable(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutPrStatusSchema = z.object({
  number: z.number().optional(),
  url: z.string(),
  title: z.string(),
  state: z.string(),
  baseRefName: z.string(),
  headRefName: z.string(),
  isMerged: z.boolean(),
  isDraft: z.boolean().optional().default(false),
  checks: z
    .array(
      z.object({
        name: z.string(),
        status: z.string(),
        url: z.string().nullable(),
        workflow: z.string().optional(),
        duration: z.string().optional(),
      }),
    )
    .optional()
    .default([]),
  checksStatus: z.string().optional(),
  reviewDecision: z.string().nullable().optional(),
  repoOwner: z.string().optional(),
  repoName: z.string().optional(),
});

export const CheckoutPrStatusResponseSchema = z.object({
  type: z.literal("checkout_pr_status_response"),
  payload: z.object({
    cwd: z.string(),
    status: CheckoutPrStatusSchema.nullable(),
    githubFeaturesEnabled: z.boolean(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

const PullRequestTimelineKnownErrorSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("not_found"),
    message: z.string().optional().default(""),
  }),
  z.object({
    kind: z.literal("forbidden"),
    message: z.string().optional().default(""),
  }),
  z.object({
    kind: z.literal("unknown"),
    message: z.string().optional().default(""),
  }),
]);

const PullRequestTimelineErrorSchema = z.preprocess((value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { kind: "unknown", message: "" };
  }
  const error = value as Record<string, unknown>;
  if (error.kind === "not_found" || error.kind === "forbidden" || error.kind === "unknown") {
    return error;
  }
  return { ...error, kind: "unknown" };
}, PullRequestTimelineKnownErrorSchema);

const PullRequestTimelineReviewItemSchema = z.object({
  id: z.string().optional().default(""),
  kind: z.literal("review"),
  author: z.string().optional().default("unknown"),
  body: z.string().optional().default(""),
  createdAt: z.number().optional().default(0),
  url: z.string().optional().default(""),
  reviewState: z
    .enum(["approved", "changes_requested", "commented"])
    .optional()
    .default("commented"),
});

const PullRequestTimelineCommentItemSchema = z.object({
  id: z.string().optional().default(""),
  kind: z.literal("comment"),
  author: z.string().optional().default("unknown"),
  body: z.string().optional().default(""),
  createdAt: z.number().optional().default(0),
  url: z.string().optional().default(""),
});

export const PullRequestTimelineItemSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }
    const item = value as Record<string, unknown>;
    if (item.kind === "review" || item.kind === "comment") {
      return item;
    }
    return { ...item, kind: "comment" };
  },
  z.discriminatedUnion("kind", [
    PullRequestTimelineReviewItemSchema,
    PullRequestTimelineCommentItemSchema,
  ]),
);

export const PullRequestTimelineResponseSchema = z.object({
  type: z.literal("pull_request_timeline_response"),
  payload: z
    .object({
      cwd: z.string().optional().default(""),
      prNumber: z.number().nullable().optional().default(null),
      items: z.array(PullRequestTimelineItemSchema).optional().default([]),
      truncated: z.boolean().optional().default(false),
      error: PullRequestTimelineErrorSchema.nullable().optional().default(null),
      requestId: z.string().optional().default(""),
      githubFeaturesEnabled: z.boolean().optional().default(true),
    })
    .optional()
    .default({}),
});

export const CheckoutSwitchBranchResponseSchema = z.object({
  type: z.literal("checkout_switch_branch_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    branch: z.string(),
    source: z.enum(["local", "remote"]).optional(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CheckoutCreateBranchResponseSchema = z.object({
  type: z.literal("checkout_create_branch_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    branch: z.string(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

const StashEntrySchema = z.object({
  index: z.number().int().min(0),
  message: z.string(),
  branch: z.string().nullable(),
  isAppostle: z.boolean(),
});

export const StashSaveResponseSchema = z.object({
  type: z.literal("stash_save_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const StashPopResponseSchema = z.object({
  type: z.literal("stash_pop_response"),
  payload: z.object({
    cwd: z.string(),
    success: z.boolean(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const StashListResponseSchema = z.object({
  type: z.literal("stash_list_response"),
  payload: z.object({
    cwd: z.string(),
    entries: z.array(StashEntrySchema),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const ValidateBranchResponseSchema = z.object({
  type: z.literal("validate_branch_response"),
  payload: z.object({
    exists: z.boolean(),
    resolvedRef: z.string().nullable(),
    isRemote: z.boolean(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const BranchSuggestionsResponseSchema = z.object({
  type: z.literal("branch_suggestions_response"),
  payload: z.object({
    branches: z.array(z.string()),
    branchDetails: z
      .array(
        z.object({
          name: z.string(),
          committerDate: z.number(),
          hasLocal: z.boolean().optional(),
          hasRemote: z.boolean().optional(),
          // True when the local branch is fully merged into the workspace's
          // base ref. Older daemons omit this — UI must treat undefined as
          // "unknown" and hide the delete affordance.
          isMergedIntoBase: z.boolean().optional(),
        }),
      )
      .optional(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const GitHubSearchResponseSchema = z.object({
  type: z.literal("github_search_response"),
  payload: z.object({
    items: z.array(GitHubSearchItemSchema),
    githubFeaturesEnabled: z.boolean(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const GitLabSearchResponseSchema = z.object({
  type: z.literal("gitlab_search_response"),
  payload: z.object({
    items: z.array(GitLabSearchItemSchema),
    // `gitlabFeaturesEnabled` mirrors the github flag — false when the daemon
    // can't find `glab` or it isn't authenticated, so the client can render a
    // "Sign in with glab" CTA instead of an empty list.
    gitlabFeaturesEnabled: z.boolean(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const CreateGitHubIssueResponseSchema = z.object({
  type: z.literal("create_github_issue_response"),
  payload: z.object({
    item: GitHubSearchItemSchema.nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const CreateGitLabIssueResponseSchema = z.object({
  type: z.literal("create_gitlab_issue_response"),
  payload: z.object({
    item: GitLabSearchItemSchema.nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const DirectorySuggestionsResponseSchema = z.object({
  type: z.literal("directory_suggestions_response"),
  payload: z.object({
    directories: z.array(z.string()),
    entries: z
      .array(
        z.object({
          path: z.string(),
          kind: z.enum(["file", "directory"]),
        }),
      )
      .optional()
      .default([]),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const ListDirectoryResponseSchema = z.object({
  type: z.literal("list_directory_response"),
  payload: z.object({
    requestId: z.string(),
    /** Resolved absolute path that was listed (e.g. "~" → "/home/pi"). */
    path: z.string(),
    /** Parent directory's absolute path, or null when at FS root. */
    parent: z.string().nullable(),
    entries: z
      .array(
        z.object({
          name: z.string(),
          path: z.string(),
          kind: z.enum(["file", "directory"]),
        }),
      )
      .default([]),
    error: z.string().nullable(),
  }),
});

export const ListFilesystemRootsResponseSchema = z.object({
  type: z.literal("list_filesystem_roots_response"),
  payload: z.object({
    requestId: z.string(),
    roots: z
      .array(
        z.object({
          path: z.string(),
          label: z.string(),
          kind: z.enum(["home", "drive", "system"]),
        }),
      )
      .default([]),
    error: z.string().nullable(),
  }),
});

const AppostleWorktreeSchema = z.object({
  worktreePath: z.string(),
  createdAt: z.string(),
  branchName: z.string().nullable().optional(),
  head: z.string().nullable().optional(),
});

export const AppostleWorktreeListResponseSchema = z.object({
  type: z.literal("appostle_worktree_list_response"),
  payload: z.object({
    worktrees: z.array(AppostleWorktreeSchema),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const AppostleWorktreeArchiveResponseSchema = z.object({
  type: z.literal("appostle_worktree_archive_response"),
  payload: z.object({
    success: z.boolean(),
    removedAgents: z.array(z.string()).optional(),
    error: CheckoutErrorSchema.nullable(),
    requestId: z.string(),
  }),
});

export const CreateAppostleWorktreeResponseSchema = z.object({
  type: z.literal("create_appostle_worktree_response"),
  payload: z.object({
    workspace: WorkspaceDescriptorPayloadSchema.nullable(),
    error: z.string().nullable(),
    errorCode: z.string().optional(),
    setupTerminalId: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const FileExplorerResponseSchema = z.object({
  type: z.literal("file_explorer_response"),
  payload: z.object({
    cwd: z.string(),
    path: z.string(),
    mode: z.enum(["list", "file"]),
    directory: FileExplorerDirectorySchema.nullable(),
    file: FileExplorerFileSchema.nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const WorkspaceSearchFilesResponseSchema = z.object({
  type: z.literal("workspace_search_files_response"),
  payload: z.object({
    cwd: z.string(),
    query: z.string(),
    results: z.array(WorkspaceSearchFilesEntrySchema),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const FileWriteResponseSchema = z.object({
  type: z.literal("file_write_response"),
  payload: z.object({
    cwd: z.string(),
    path: z.string(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const FileDeleteResponseSchema = z.object({
  type: z.literal("file_delete_response"),
  payload: z.object({
    cwd: z.string(),
    path: z.string(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const FileExplorerDeleteResponseSchema = z.object({
  type: z.literal("file_explorer_delete_response"),
  payload: z.object({
    cwd: z.string(),
    path: z.string(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const FileMkdirResponseSchema = z.object({
  type: z.literal("file_mkdir_response"),
  payload: z.object({
    cwd: z.string(),
    path: z.string(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const FileMoveResponseSchema = z.object({
  type: z.literal("file_move_response"),
  payload: z.object({
    cwd: z.string(),
    sourcePath: z.string(),
    destinationPath: z.string(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

const ProjectIconSchema = z.object({
  data: z.string(),
  mimeType: z.string(),
});

export const ProjectIconResponseSchema = z.object({
  type: z.literal("project_icon_response"),
  payload: z.object({
    cwd: z.string(),
    icon: ProjectIconSchema.nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const FileDownloadTokenResponseSchema = z.object({
  type: z.literal("file_download_token_response"),
  payload: z.object({
    cwd: z.string(),
    path: z.string(),
    token: z.string().nullable(),
    fileName: z.string().nullable(),
    mimeType: z.string().nullable(),
    size: z.number().nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const ListProviderModelsResponseMessageSchema = z.object({
  type: z.literal("list_provider_models_response"),
  payload: z.object({
    provider: AgentProviderSchema,
    models: z.array(AgentModelDefinitionSchema).optional(),
    error: z.string().nullable().optional(),
    fetchedAt: z.string(),
    requestId: z.string(),
  }),
});

export const ListProviderModesResponseMessageSchema = z.object({
  type: z.literal("list_provider_modes_response"),
  payload: z.object({
    provider: AgentProviderSchema,
    modes: z.array(AgentModeSchema).optional(),
    error: z.string().nullable().optional(),
    fetchedAt: z.string(),
    requestId: z.string(),
  }),
});

export const ListProviderFeaturesResponseMessageSchema = z.object({
  type: z.literal("list_provider_features_response"),
  payload: z.object({
    provider: AgentProviderSchema,
    features: z.array(AgentFeatureSchema).optional(),
    error: z.string().nullable().optional(),
    fetchedAt: z.string(),
    requestId: z.string(),
  }),
});

const ProviderAvailabilitySchema = z.object({
  provider: AgentProviderSchema,
  available: z.boolean(),
  error: z.string().nullable().optional(),
});

export const ListAvailableProvidersResponseSchema = z.object({
  type: z.literal("list_available_providers_response"),
  payload: z.object({
    providers: z.array(ProviderAvailabilitySchema),
    error: z.string().nullable().optional(),
    fetchedAt: z.string(),
    requestId: z.string(),
  }),
});

// COMPAT(providersSnapshot): added in v0.1.48, remove gating when all clients use snapshot
export const GetProvidersSnapshotResponseMessageSchema = z.object({
  type: z.literal("get_providers_snapshot_response"),
  payload: z.object({
    entries: z.array(ProviderSnapshotEntrySchema),
    generatedAt: z.string(),
    requestId: z.string(),
  }),
});

// COMPAT(providersSnapshot): added in v0.1.48, remove gating when all clients use snapshot
export const ProvidersSnapshotUpdateMessageSchema = z.object({
  type: z.literal("providers_snapshot_update"),
  payload: z.object({
    cwd: z.string().optional(),
    entries: z.array(ProviderSnapshotEntrySchema),
    generatedAt: z.string(),
  }),
});

// COMPAT(providersSnapshot): added in v0.1.48, remove gating when all clients use snapshot
export const RefreshProvidersSnapshotResponseMessageSchema = z.object({
  type: z.literal("refresh_providers_snapshot_response"),
  payload: z.object({
    requestId: z.string(),
    acknowledged: z.boolean(),
  }),
});

// COMPAT(providersSnapshot): added in v0.1.48, remove gating when all clients use snapshot
export const ProviderDiagnosticResponseMessageSchema = z.object({
  type: z.literal("provider_diagnostic_response"),
  payload: z.object({
    provider: AgentProviderSchema,
    diagnostic: z.string(),
    requestId: z.string(),
  }),
});

const AgentSlashCommandSchema = z.object({
  name: z.string(),
  description: z.string(),
  argumentHint: z.string(),
});

export const ListCommandsResponseSchema = z.object({
  type: z.literal("list_commands_response"),
  payload: z.object({
    agentId: z.string(),
    commands: z.array(AgentSlashCommandSchema),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

// ============================================================================
// Skills response messages
// ============================================================================

const SkillEntrySchema = z.object({
  id: z.string(),
  provider: SkillProviderSchema,
  scope: SkillScopeSchema,
  name: z.string(),
  path: z.string(),
  description: z.string(),
  modifiedAt: z.string(),
  size: z.number(),
});

export const ListSkillsResponseSchema = z.object({
  type: z.literal("list_skills_response"),
  payload: z.object({
    entries: z.array(SkillEntrySchema),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const CreateSkillResponseSchema = z.object({
  type: z.literal("create_skill_response"),
  payload: z.object({
    /** Absolute path of the created SKILL.md (or prompt .md). Empty when error is set. */
    path: z.string(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const WriteSkillFrontmatterResponseSchema = z.object({
  type: z.literal("write_skill_frontmatter_response"),
  payload: z.object({
    path: z.string(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

// ============================================================================
// Terminal Outbound Messages
// ============================================================================

const TerminalInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  cwd: z.string(),
  title: z.string().optional(),
});

export const TerminalCellSchema = z
  .object({
    char: z.string(),
    fg: z.number().optional(),
    bg: z.number().optional(),
    fgMode: z.number().optional(),
    bgMode: z.number().optional(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    dim: z.boolean().optional(),
    inverse: z.boolean().optional(),
    strikethrough: z.boolean().optional(),
  })
  .strict();

export const TerminalCursorStyleSchema = z.enum(["block", "underline", "bar"]);

export const TerminalCursorSchema = z
  .object({
    row: z.number(),
    col: z.number(),
    hidden: z.boolean().optional(),
    style: TerminalCursorStyleSchema.optional(),
    blink: z.boolean().optional(),
  })
  .strict();

export const TerminalStateSchema = z
  .object({
    rows: z.number(),
    cols: z.number(),
    grid: z.array(z.array(TerminalCellSchema)),
    scrollback: z.array(z.array(TerminalCellSchema)),
    cursor: TerminalCursorSchema,
    title: z.string().optional(),
  })
  .strict();

export const ListTerminalsResponseSchema = z.object({
  type: z.literal("list_terminals_response"),
  payload: z.object({
    cwd: z.string().optional(),
    terminals: z.array(TerminalInfoSchema.omit({ cwd: true })),
    requestId: z.string(),
  }),
});

export const TerminalsChangedSchema = z.object({
  type: z.literal("terminals_changed"),
  payload: z.object({
    cwd: z.string(),
    terminals: z.array(TerminalInfoSchema.omit({ cwd: true })),
  }),
});

export const CreateTerminalResponseSchema = z.object({
  type: z.literal("create_terminal_response"),
  payload: z.object({
    terminal: TerminalInfoSchema.nullable(),
    error: z.string().nullable(),
    requestId: z.string(),
  }),
});

export const SubscribeTerminalResponseSchema = z.object({
  type: z.literal("subscribe_terminal_response"),
  payload: z.union([
    z.object({
      terminalId: z.string(),
      slot: z.number().int().min(0).max(255),
      error: z.null(),
      requestId: z.string(),
    }),
    z.object({
      terminalId: z.string(),
      error: z.string(),
      requestId: z.string(),
    }),
  ]),
});

export const KillTerminalResponseSchema = z.object({
  type: z.literal("kill_terminal_response"),
  payload: z.object({
    terminalId: z.string(),
    success: z.boolean(),
    requestId: z.string(),
  }),
});

export const CaptureTerminalResponseSchema = z.object({
  type: z.literal("capture_terminal_response"),
  payload: z.object({
    terminalId: z.string(),
    lines: z.array(z.string()),
    totalLines: z.number().int().nonnegative(),
    requestId: z.string(),
  }),
});

export const TerminalStreamExitSchema = z.object({
  type: z.literal("terminal_stream_exit"),
  payload: z.object({
    terminalId: z.string(),
  }),
});

/**
 * Hydration push — tell the client to refetch its tab store
 * (project list, stacks, tabs). Emitted by the daemon after share /
 * unshare / global-archive flows so the recipient(s) see the new
 * state without waiting for the 30-second polling cycle.
 *
 * Coarse-grained on purpose: the client always re-runs `syncAll(machineIds, token)`
 * on receipt. Targeted refresh (project-scoped) is a later optimisation.
 */
export const TabSyncInvalidateMessageSchema = z.object({
  type: z.literal("tab_sync_invalidate"),
  reason: z
    .enum([
      "share-created",
      "share-revoked",
      "agent-archived",
      "handoff-spawned",
      "project-removed",
      "agent-promoted",
      "workspace-unarchived",
      "schedule-spawned",
    ])
    .optional(),
});

/**
 * Targeted push — daemon just created a new tab on the auth-server and
 * forwards the serialized body inline so the client applies it directly,
 * with zero round-trips. Sibling of `tab_sync_invalidate` but optimised
 * for the "I know exactly what changed" case (handoff-spawned today;
 * future: pair-from-link, open-project, etc).
 *
 * Client behaviour: cast `tab` to RemoteTab and apply via the tab-store's
 * optimistic-insert path. No syncAll triggered.
 *
 * `tab` is `unknown` at the schema layer: the auth-server's serializeTab
 * shape is the contract, and we don't want to maintain a duplicate Zod
 * schema for RemoteTab in this file. Old clients that don't know this
 * message type fall back to the next `tab_sync_invalidate` poll.
 */
export const TabAppearedMessageSchema = z.object({
  type: z.literal("tab_appeared"),
  projectId: z.string(),
  tab: z.unknown(),
});

export const SessionOutboundMessageSchema = z.discriminatedUnion("type", [
  TabSyncInvalidateMessageSchema,
  TabAppearedMessageSchema,
  ActivityLogMessageSchema,
  AssistantChunkMessageSchema,
  AudioOutputMessageSchema,
  TranscriptionResultMessageSchema,
  VoiceInputStateMessageSchema,
  DictationStreamAckMessageSchema,
  DictationStreamFinishAcceptedMessageSchema,
  DictationStreamPartialMessageSchema,
  DictationStreamFinalMessageSchema,
  DictationStreamErrorMessageSchema,
  StatusMessageSchema,
  PongMessageSchema,
  RpcErrorMessageSchema,
  ArtifactMessageSchema,
  AgentUpdateMessageSchema,
  WorkspaceUpdateMessageSchema,
  ScriptStatusUpdateMessageSchema,
  WorkspaceSetupProgressMessageSchema,
  WorkspaceSetupStatusResponseMessageSchema,
  AgentStreamMessageSchema,
  AgentStatusMessageSchema,
  FetchAgentsResponseMessageSchema,
  InitialAgentSnapshotMessageSchema,
  FetchWorkspacesResponseMessageSchema,
  OpenProjectResponseMessageSchema,
  InitRepoResponseMessageSchema,
  CloneRepoResponseMessageSchema,
  StartWorkspaceScriptResponseMessageSchema,
  ListAvailableEditorsResponseMessageSchema,
  OpenInEditorResponseMessageSchema,
  ArchiveWorkspaceResponseMessageSchema,
  FetchAgentResponseMessageSchema,
  FetchAgentTimelineResponseMessageSchema,
  CancelAgentResponseMessageSchema,
  ClearAgentAttentionResponseMessageSchema,
  SendAgentMessageResponseMessageSchema,
  SetVoiceModeResponseMessageSchema,
  GetDaemonConfigResponseMessageSchema,
  SetDaemonConfigResponseMessageSchema,
  SetAgentModeResponseMessageSchema,
  SetAgentModelResponseMessageSchema,
  SetAgentThinkingResponseMessageSchema,
  SetAgentFeatureResponseMessageSchema,
  ListAgentMcpServersResponseMessageSchema,
  SetAgentMcpServerResponseMessageSchema,
  UpdateAgentResponseMessageSchema,
  WorkspaceStacksStateSchema,
  StackCreateResponseMessageSchema,
  StackRenameResponseMessageSchema,
  StackDeleteResponseMessageSchema,
  StackReorderResponseMessageSchema,
  StackSetActiveResponseMessageSchema,
  WaitForFinishResponseMessageSchema,
  AgentPermissionRequestMessageSchema,
  AgentPermissionResolvedMessageSchema,
  AgentDeletedMessageSchema,
  AgentArchivedMessageSchema,
  ForkAgentResponseMessageSchema,
  RewindAgentResponseMessageSchema,
  CloseItemsResponseSchema,
  CheckoutStatusResponseSchema,
  SubscribeCheckoutDiffResponseSchema,
  CheckoutDiffUpdateSchema,
  CheckoutCommitResponseSchema,
  CheckoutGenerateCommitMessageResponseSchema,
  CheckoutMergeResponseSchema,
  CheckoutMergeFromBaseResponseSchema,
  CheckoutPullResponseSchema,
  CheckoutPushResponseSchema,
  CheckoutDeleteBranchResponseSchema,
  CheckoutPrCreateResponseSchema,
  CheckoutPrStatusResponseSchema,
  PullRequestTimelineResponseSchema,
  CheckoutSwitchBranchResponseSchema,
  CheckoutCreateBranchResponseSchema,
  StashSaveResponseSchema,
  StashPopResponseSchema,
  StashListResponseSchema,
  ValidateBranchResponseSchema,
  BranchSuggestionsResponseSchema,
  GitHubSearchResponseSchema,
  GitLabSearchResponseSchema,
  CreateGitHubIssueResponseSchema,
  CreateGitLabIssueResponseSchema,
  OAuthInitiateResponseSchema,
  OAuthCompleteResponseSchema,
  OAuthStatusResponseSchema,
  OAuthSignOutResponseSchema,
  DirectorySuggestionsResponseSchema,
  ListDirectoryResponseSchema,
  ListFilesystemRootsResponseSchema,
  AppostleWorktreeListResponseSchema,
  AppostleWorktreeArchiveResponseSchema,
  CreateAppostleWorktreeResponseSchema,
  FileExplorerResponseSchema,
  WorkspaceSearchFilesResponseSchema,
  FileWriteResponseSchema,
  FileDeleteResponseSchema,
  FileExplorerDeleteResponseSchema,
  FileMkdirResponseSchema,
  FileMoveResponseSchema,
  ProjectIconResponseSchema,
  FileDownloadTokenResponseSchema,
  ListProviderModelsResponseMessageSchema,
  ListProviderModesResponseMessageSchema,
  ListProviderFeaturesResponseMessageSchema,
  ListAvailableProvidersResponseSchema,
  GetProvidersSnapshotResponseMessageSchema,
  ProvidersSnapshotUpdateMessageSchema,
  RefreshProvidersSnapshotResponseMessageSchema,
  ProviderDiagnosticResponseMessageSchema,
  ListCommandsResponseSchema,
  ListSkillsResponseSchema,
  CreateSkillResponseSchema,
  WriteSkillFrontmatterResponseSchema,
  RolesListScopeResponseSchema,
  RoleCreateResponseSchema,
  RoleWriteFrontmatterResponseSchema,
  RoleMoveResponseSchema,
  ListTerminalsResponseSchema,
  TerminalsChangedSchema,
  CreateTerminalResponseSchema,
  SubscribeTerminalResponseSchema,
  KillTerminalResponseSchema,
  CaptureTerminalResponseSchema,
  TerminalStreamExitSchema,
  ChatCreateResponseSchema,
  ChatListResponseSchema,
  ChatInspectResponseSchema,
  ChatDeleteResponseSchema,
  ChatPostResponseSchema,
  ChatReadResponseSchema,
  ChatWaitResponseSchema,
  PreviewOpenResponseSchema,
  PreviewFrameResponseSchema,
  PreviewScrollResponseSchema,
  PreviewPickResponseSchema,
  PreviewCloseResponseSchema,
  PreviewResizeResponseSchema,
  PreviewFramePushSchema,
  ScheduleCreateResponseSchema,
  ScheduleListResponseSchema,
  ScheduleInspectResponseSchema,
  ScheduleLogsResponseSchema,
  SchedulePauseResponseSchema,
  ScheduleResumeResponseSchema,
  ScheduleDeleteResponseSchema,
  LoopRunResponseSchema,
  LoopListResponseSchema,
  LoopInspectResponseSchema,
  LoopLogsResponseSchema,
  LoopStopResponseSchema,
  QuestRunResponseSchema,
  QuestListResponseSchema,
  QuestInspectResponseSchema,
  QuestStopResponseSchema,
  RolesListResponseSchema,
  BrandsListScopeResponseSchema,
  BrandCreateResponseSchema,
  BrandWriteFrontmatterResponseSchema,
  BrandAssetCopyResponseSchema,
  BrandAssetUploadResponseSchema,
  BrandAssetDeleteResponseSchema,
  BrandAssetRenameResponseSchema,
  BrandGenerateTokensResponseSchema,
  BrandTokenGenerationStatusResponseSchema,
  BrandGenerateLayoutResponseSchema,
  BrandLayoutQaNextResponseSchema,
  BrandGenerateWireframeResponseSchema,
  BrandAnalyzePhotographyResponseSchema,
  RightFontLibraryResponseSchema,
  GoogleFontsCatalogResponseSchema,
  GoogleFontsDownloadResponseSchema,
  GetVapidPublicKeyResponseSchema,
  LinkAccountResponseMessageSchema,
  DaemonAwaitingLinkResponseMessageSchema,
  ClaudeTokenSetResponseMessageSchema,
  ClaudeTokenStatusResponseMessageSchema,
  ClaudeTokenRemoveResponseMessageSchema,
  ListSessionUploadsResponseSchema,
  DeleteSessionUploadResponseSchema,
  ListSessionImagesResponseSchema,
  DeleteSessionImageResponseSchema,
  FetchAttachmentBytesResponseSchema,
  PlanUsageResponseSchema,
  TodoListStateSchema,
  TodoRemoveResponseSchema,
  TodoSetCompletedResponseSchema,
  TodoReorderResponseSchema,
  TodoAddResponseSchema,
  TodoNotesUpdateResponseSchema,
  TodoLinkedAgentUpdateResponseSchema,
  TodoAssigneeUpdateResponseSchema,
  TodoUpdateTextResponseSchema,
  TodoSubtaskAddResponseSchema,
  TodoSubtaskUpdateTextResponseSchema,
  TodoSubtaskRemoveResponseSchema,
  TodoSubtaskSetCompletedResponseSchema,
  TodoSubtaskReorderResponseSchema,
  TodoStatusUpdateResponseSchema,
  TodoPriorityUpdateResponseSchema,
  TodoScheduleUpdateResponseSchema,
  TodoBlockedByUpdateResponseSchema,
  TodoResultReportUpdateResponseSchema,
  TodoActivityAddResponseSchema,
  TodoActivityListResponseSchema,
  TodoSessionsListResponseSchema,
  TodoSubtaskNotesUpdateResponseSchema,
  TodoSubtaskAssigneeUpdateResponseSchema,
  TodoSubtaskStatusUpdateResponseSchema,
  TodoSubtaskScheduleUpdateResponseSchema,
  TodoSubtaskPriorityUpdateResponseSchema,
  TodoSubtaskBlockedByUpdateResponseSchema,
  ShareSessionResponseMessageSchema,
  UnshareSessionResponseMessageSchema,
]);

export type SessionOutboundMessage = z.infer<typeof SessionOutboundMessageSchema>;

// Type exports for individual message types
export type ActivityLogMessage = z.infer<typeof ActivityLogMessageSchema>;
export type AssistantChunkMessage = z.infer<typeof AssistantChunkMessageSchema>;
export type AudioOutputMessage = z.infer<typeof AudioOutputMessageSchema>;
export type TranscriptionResultMessage = z.infer<typeof TranscriptionResultMessageSchema>;
export type StatusMessage = z.infer<typeof StatusMessageSchema>;
export type ServerCapabilityState = z.infer<typeof ServerCapabilityStateSchema>;
export type ServerVoiceCapabilities = z.infer<typeof ServerVoiceCapabilitiesSchema>;
export type ServerCapabilities = z.infer<typeof ServerCapabilitiesSchema>;
export type ServerInfoStatusPayload = z.infer<typeof ServerInfoStatusPayloadSchema>;
export type RpcErrorMessage = z.infer<typeof RpcErrorMessageSchema>;
export type ArtifactMessage = z.infer<typeof ArtifactMessageSchema>;
export type AgentUpdateMessage = z.infer<typeof AgentUpdateMessageSchema>;
export type WorkspaceSetupProgressMessage = z.infer<typeof WorkspaceSetupProgressMessageSchema>;
export type WorkspaceSetupSnapshot = z.infer<typeof WorkspaceSetupSnapshotSchema>;
export type WorkspaceSetupStatusResponseMessage = z.infer<
  typeof WorkspaceSetupStatusResponseMessageSchema
>;
export type AgentStreamMessage = z.infer<typeof AgentStreamMessageSchema>;
export type AgentStatusMessage = z.infer<typeof AgentStatusMessageSchema>;
export type ProjectCheckoutLitePayload = z.infer<typeof ProjectCheckoutLitePayloadSchema>;
export type ProjectPlacementPayload = z.infer<typeof ProjectPlacementPayloadSchema>;
export type WorkspaceStateBucket = z.infer<typeof WorkspaceStateBucketSchema>;
export type WorkspaceDescriptorPayload = z.infer<typeof WorkspaceDescriptorPayloadSchema>;
export type WorkspaceScriptLifecycle = z.infer<typeof WorkspaceScriptLifecycleSchema>;
export type WorkspaceScriptHealth = z.infer<typeof WorkspaceScriptHealthSchema>;
export type WorkspaceScriptPayload = z.infer<typeof WorkspaceScriptPayloadSchema>;
export type KnownEditorTargetId = z.infer<typeof KnownEditorTargetIdSchema>;
export type LegacyEditorTargetId = z.infer<typeof LegacyEditorTargetIdSchema>;
export type EditorTargetId = LiteralUnion<KnownEditorTargetId, string>;
export type EditorTargetDescriptorPayload = z.infer<typeof EditorTargetDescriptorPayloadSchema>;
export type FetchAgentsResponseMessage = z.infer<typeof FetchAgentsResponseMessageSchema>;
export type InitialAgentSnapshotMessage = z.infer<typeof InitialAgentSnapshotMessageSchema>;
export type FetchWorkspacesResponseMessage = z.infer<typeof FetchWorkspacesResponseMessageSchema>;
export type ScriptStatusUpdateMessage = z.infer<typeof ScriptStatusUpdateMessageSchema>;
export type OpenProjectResponseMessage = z.infer<typeof OpenProjectResponseMessageSchema>;
export type InitRepoResponseMessage = z.infer<typeof InitRepoResponseMessageSchema>;
export type CloneRepoResponseMessage = z.infer<typeof CloneRepoResponseMessageSchema>;
export type StartWorkspaceScriptResponseMessage = z.infer<
  typeof StartWorkspaceScriptResponseMessageSchema
>;
export type ListAvailableEditorsResponseMessage = z.infer<
  typeof ListAvailableEditorsResponseMessageSchema
>;
export type OpenInEditorResponseMessage = z.infer<typeof OpenInEditorResponseMessageSchema>;
export type ArchiveWorkspaceResponseMessage = z.infer<typeof ArchiveWorkspaceResponseMessageSchema>;
export type FetchAgentResponseMessage = z.infer<typeof FetchAgentResponseMessageSchema>;
export type FetchAgentTimelineResponseMessage = z.infer<
  typeof FetchAgentTimelineResponseMessageSchema
>;
export type CancelAgentResponseMessage = z.infer<typeof CancelAgentResponseMessageSchema>;
export type SendAgentMessageResponseMessage = z.infer<typeof SendAgentMessageResponseMessageSchema>;
export type SetVoiceModeResponseMessage = z.infer<typeof SetVoiceModeResponseMessageSchema>;
export type SetAgentModeResponseMessage = z.infer<typeof SetAgentModeResponseMessageSchema>;
export type SetAgentModelResponseMessage = z.infer<typeof SetAgentModelResponseMessageSchema>;
export type SetAgentThinkingResponseMessage = z.infer<typeof SetAgentThinkingResponseMessageSchema>;
export type SetAgentFeatureResponseMessage = z.infer<typeof SetAgentFeatureResponseMessageSchema>;
export type ListAgentMcpServersRequestMessage = z.infer<
  typeof ListAgentMcpServersRequestMessageSchema
>;
export type ListAgentMcpServersResponseMessage = z.infer<
  typeof ListAgentMcpServersResponseMessageSchema
>;
export type SetAgentMcpServerRequestMessage = z.infer<typeof SetAgentMcpServerRequestMessageSchema>;
export type SetAgentMcpServerResponseMessage = z.infer<
  typeof SetAgentMcpServerResponseMessageSchema
>;
export type UpdateAgentResponseMessage = z.infer<typeof UpdateAgentResponseMessageSchema>;
export type WaitForFinishResponseMessage = z.infer<typeof WaitForFinishResponseMessageSchema>;
export type AgentPermissionRequestMessage = z.infer<typeof AgentPermissionRequestMessageSchema>;
export type AgentPermissionResolvedMessage = z.infer<typeof AgentPermissionResolvedMessageSchema>;
export type AgentDeletedMessage = z.infer<typeof AgentDeletedMessageSchema>;
export type ListProviderModelsResponseMessage = z.infer<
  typeof ListProviderModelsResponseMessageSchema
>;
export type ListProviderModesResponseMessage = z.infer<
  typeof ListProviderModesResponseMessageSchema
>;
export type ListProviderFeaturesResponseMessage = z.infer<
  typeof ListProviderFeaturesResponseMessageSchema
>;
export type ListAvailableProvidersResponse = z.infer<typeof ListAvailableProvidersResponseSchema>;
export type GetProvidersSnapshotResponseMessage = z.infer<
  typeof GetProvidersSnapshotResponseMessageSchema
>;
export type ProvidersSnapshotUpdateMessage = z.infer<typeof ProvidersSnapshotUpdateMessageSchema>;
export type RefreshProvidersSnapshotResponseMessage = z.infer<
  typeof RefreshProvidersSnapshotResponseMessageSchema
>;
export type ProviderDiagnosticResponseMessage = z.infer<
  typeof ProviderDiagnosticResponseMessageSchema
>;
export type ChatCreateResponse = z.infer<typeof ChatCreateResponseSchema>;
export type ChatListResponse = z.infer<typeof ChatListResponseSchema>;
export type ChatInspectResponse = z.infer<typeof ChatInspectResponseSchema>;
export type ChatDeleteResponse = z.infer<typeof ChatDeleteResponseSchema>;
export type ChatPostResponse = z.infer<typeof ChatPostResponseSchema>;
export type ChatReadResponse = z.infer<typeof ChatReadResponseSchema>;
export type ChatWaitResponse = z.infer<typeof ChatWaitResponseSchema>;
export type ScheduleCreateResponse = z.infer<typeof ScheduleCreateResponseSchema>;
export type ScheduleListResponse = z.infer<typeof ScheduleListResponseSchema>;
export type ScheduleInspectResponse = z.infer<typeof ScheduleInspectResponseSchema>;
export type ScheduleLogsResponse = z.infer<typeof ScheduleLogsResponseSchema>;
export type SchedulePauseResponse = z.infer<typeof SchedulePauseResponseSchema>;
export type ScheduleResumeResponse = z.infer<typeof ScheduleResumeResponseSchema>;
export type ScheduleDeleteResponse = z.infer<typeof ScheduleDeleteResponseSchema>;
export type LoopRunResponse = z.infer<typeof LoopRunResponseSchema>;
export type LoopListResponse = z.infer<typeof LoopListResponseSchema>;
export type LoopInspectResponse = z.infer<typeof LoopInspectResponseSchema>;
export type LoopLogsResponse = z.infer<typeof LoopLogsResponseSchema>;
export type LoopStopResponse = z.infer<typeof LoopStopResponseSchema>;

// Type exports for payload types
export type ActivityLogPayload = z.infer<typeof ActivityLogPayloadSchema>;

// Type exports for inbound message types
export type VoiceAudioChunkMessage = z.infer<typeof VoiceAudioChunkMessageSchema>;
export type FetchAgentsRequestMessage = z.infer<typeof FetchAgentsRequestMessageSchema>;
export type FetchWorkspacesRequestMessage = z.infer<typeof FetchWorkspacesRequestMessageSchema>;
export type FetchAgentRequestMessage = z.infer<typeof FetchAgentRequestMessageSchema>;
export type SendAgentMessageRequest = z.infer<typeof SendAgentMessageRequestSchema>;
export type WaitForFinishRequest = z.infer<typeof WaitForFinishRequestSchema>;
export type DictationStreamStartMessage = z.infer<typeof DictationStreamStartMessageSchema>;
export type DictationStreamChunkMessage = z.infer<typeof DictationStreamChunkMessageSchema>;
export type DictationStreamFinishMessage = z.infer<typeof DictationStreamFinishMessageSchema>;
export type DictationStreamCancelMessage = z.infer<typeof DictationStreamCancelMessageSchema>;
export type CreateAgentRequestMessage = z.infer<typeof CreateAgentRequestMessageSchema>;
export type AgentAttachment = z.infer<typeof AgentAttachmentSchema>;
export type FileAttachment = z.infer<typeof FileAttachmentSchema>;
export type SessionUpload = z.infer<typeof SessionUploadSchema>;
export type ListSessionUploadsRequest = z.infer<typeof ListSessionUploadsRequestSchema>;
export type ListSessionUploadsResponse = z.infer<typeof ListSessionUploadsResponseSchema>;
export type DeleteSessionUploadRequest = z.infer<typeof DeleteSessionUploadRequestSchema>;
export type DeleteSessionUploadResponse = z.infer<typeof DeleteSessionUploadResponseSchema>;
export type SessionImageEntry = z.infer<typeof SessionImageSchema>;
export type ListSessionImagesRequest = z.infer<typeof ListSessionImagesRequestSchema>;
export type ListSessionImagesResponse = z.infer<typeof ListSessionImagesResponseSchema>;
export type DeleteSessionImageRequest = z.infer<typeof DeleteSessionImageRequestSchema>;
export type DeleteSessionImageResponse = z.infer<typeof DeleteSessionImageResponseSchema>;
export type FetchAttachmentBytesRequest = z.infer<typeof FetchAttachmentBytesRequestSchema>;
export type FetchAttachmentBytesResponse = z.infer<typeof FetchAttachmentBytesResponseSchema>;
export type ListProviderModelsRequestMessage = z.infer<
  typeof ListProviderModelsRequestMessageSchema
>;
export type ListProviderModesRequestMessage = z.infer<typeof ListProviderModesRequestMessageSchema>;
export type ListProviderFeaturesRequestMessage = z.infer<
  typeof ListProviderFeaturesRequestMessageSchema
>;
export type ListAvailableProvidersRequestMessage = z.infer<
  typeof ListAvailableProvidersRequestMessageSchema
>;
export type GetProvidersSnapshotRequestMessage = z.infer<
  typeof GetProvidersSnapshotRequestMessageSchema
>;
export type RefreshProvidersSnapshotRequestMessage = z.infer<
  typeof RefreshProvidersSnapshotRequestMessageSchema
>;
export type ProviderDiagnosticRequestMessage = z.infer<
  typeof ProviderDiagnosticRequestMessageSchema
>;
export type ChatCreateRequest = z.infer<typeof ChatCreateRequestSchema>;
export type ChatListRequest = z.infer<typeof ChatListRequestSchema>;
export type ChatInspectRequest = z.infer<typeof ChatInspectRequestSchema>;
export type ChatDeleteRequest = z.infer<typeof ChatDeleteRequestSchema>;
export type ChatPostRequest = z.infer<typeof ChatPostRequestSchema>;
export type ChatReadRequest = z.infer<typeof ChatReadRequestSchema>;
export type ChatWaitRequest = z.infer<typeof ChatWaitRequestSchema>;
export type ScheduleCreateRequest = z.infer<typeof ScheduleCreateRequestSchema>;
export type ScheduleListRequest = z.infer<typeof ScheduleListRequestSchema>;
export type ScheduleInspectRequest = z.infer<typeof ScheduleInspectRequestSchema>;
export type ScheduleLogsRequest = z.infer<typeof ScheduleLogsRequestSchema>;
export type SchedulePauseRequest = z.infer<typeof SchedulePauseRequestSchema>;
export type ScheduleResumeRequest = z.infer<typeof ScheduleResumeRequestSchema>;
export type ScheduleDeleteRequest = z.infer<typeof ScheduleDeleteRequestSchema>;
export type LoopRunRequest = z.infer<typeof LoopRunRequestSchema>;
export type LoopListRequest = z.infer<typeof LoopListRequestSchema>;
export type LoopInspectRequest = z.infer<typeof LoopInspectRequestSchema>;
export type LoopLogsRequest = z.infer<typeof LoopLogsRequestSchema>;
export type LoopStopRequest = z.infer<typeof LoopStopRequestSchema>;
export type QuestRunRequest = z.infer<typeof QuestRunRequestSchema>;
export type QuestListRequest = z.infer<typeof QuestListRequestSchema>;
export type QuestInspectRequest = z.infer<typeof QuestInspectRequestSchema>;
export type QuestStopRequest = z.infer<typeof QuestStopRequestSchema>;
export type QuestRunResponse = z.infer<typeof QuestRunResponseSchema>;
export type QuestListResponse = z.infer<typeof QuestListResponseSchema>;
export type QuestInspectResponse = z.infer<typeof QuestInspectResponseSchema>;
export type QuestStopResponse = z.infer<typeof QuestStopResponseSchema>;
export type QuestStreamMessage = z.infer<typeof QuestStreamMessageSchema>;
export type RolesListRequest = z.infer<typeof RolesListRequestSchema>;
export type RolesListResponse = z.infer<typeof RolesListResponseSchema>;
export type RolesListScopeRequest = z.infer<typeof RolesListScopeRequestSchema>;
export type RolesListScopeResponse = z.infer<typeof RolesListScopeResponseSchema>;
export type RoleCreateRequest = z.infer<typeof RoleCreateRequestSchema>;
export type RoleCreateResponse = z.infer<typeof RoleCreateResponseSchema>;
export type RoleWriteFrontmatterRequest = z.infer<typeof RoleWriteFrontmatterRequestSchema>;
export type RoleWriteFrontmatterResponse = z.infer<typeof RoleWriteFrontmatterResponseSchema>;
export type ResumeAgentRequestMessage = z.infer<typeof ResumeAgentRequestMessageSchema>;
export type DeleteAgentRequestMessage = z.infer<typeof DeleteAgentRequestMessageSchema>;
export type UpdateAgentRequestMessage = z.infer<typeof UpdateAgentRequestMessageSchema>;
export type SetAgentModeRequestMessage = z.infer<typeof SetAgentModeRequestMessageSchema>;
export type SetAgentModelRequestMessage = z.infer<typeof SetAgentModelRequestMessageSchema>;
export type SetAgentThinkingRequestMessage = z.infer<typeof SetAgentThinkingRequestMessageSchema>;
export type SetAgentFeatureRequestMessage = z.infer<typeof SetAgentFeatureRequestMessageSchema>;
export type AgentPermissionResponseMessage = z.infer<typeof AgentPermissionResponseMessageSchema>;
export type CheckoutStatusRequest = z.infer<typeof CheckoutStatusRequestSchema>;
export type CheckoutStatusResponse = z.infer<typeof CheckoutStatusResponseSchema>;
export type SubscribeCheckoutDiffRequest = z.infer<typeof SubscribeCheckoutDiffRequestSchema>;
export type UnsubscribeCheckoutDiffRequest = z.infer<typeof UnsubscribeCheckoutDiffRequestSchema>;
export type SubscribeCheckoutDiffResponse = z.infer<typeof SubscribeCheckoutDiffResponseSchema>;
export type CheckoutDiffUpdate = z.infer<typeof CheckoutDiffUpdateSchema>;
export type CheckoutCommitRequest = z.infer<typeof CheckoutCommitRequestSchema>;
export type CheckoutCommitResponse = z.infer<typeof CheckoutCommitResponseSchema>;
export type CheckoutGenerateCommitMessageRequest = z.infer<
  typeof CheckoutGenerateCommitMessageRequestSchema
>;
export type CheckoutGenerateCommitMessageResponse = z.infer<
  typeof CheckoutGenerateCommitMessageResponseSchema
>;
export type CheckoutMergeRequest = z.infer<typeof CheckoutMergeRequestSchema>;
export type CheckoutMergeResponse = z.infer<typeof CheckoutMergeResponseSchema>;
export type CheckoutMergeFromBaseRequest = z.infer<typeof CheckoutMergeFromBaseRequestSchema>;
export type CheckoutMergeFromBaseResponse = z.infer<typeof CheckoutMergeFromBaseResponseSchema>;
export type CheckoutPullRequest = z.infer<typeof CheckoutPullRequestSchema>;
export type CheckoutPullResponse = z.infer<typeof CheckoutPullResponseSchema>;
export type CheckoutPushRequest = z.infer<typeof CheckoutPushRequestSchema>;
export type CheckoutPushResponse = z.infer<typeof CheckoutPushResponseSchema>;
export type CheckoutDeleteBranchRequest = z.infer<typeof CheckoutDeleteBranchRequestSchema>;
export type CheckoutDeleteBranchResponse = z.infer<typeof CheckoutDeleteBranchResponseSchema>;
export type CheckoutPrCreateRequest = z.infer<typeof CheckoutPrCreateRequestSchema>;
export type CheckoutPrCreateResponse = z.infer<typeof CheckoutPrCreateResponseSchema>;
export type CheckoutPrStatusRequest = z.infer<typeof CheckoutPrStatusRequestSchema>;
export type CheckoutPrStatusResponse = z.infer<typeof CheckoutPrStatusResponseSchema>;
export type PullRequestTimelineRequest = z.infer<typeof PullRequestTimelineRequestSchema>;
export type PullRequestTimelineItem = z.infer<typeof PullRequestTimelineItemSchema>;
export type PullRequestTimelineResponse = z.infer<typeof PullRequestTimelineResponseSchema>;
export type CheckoutSwitchBranchRequest = z.infer<typeof CheckoutSwitchBranchRequestSchema>;
export type CheckoutSwitchBranchResponse = z.infer<typeof CheckoutSwitchBranchResponseSchema>;
export type CheckoutCreateBranchRequest = z.infer<typeof CheckoutCreateBranchRequestSchema>;
export type CheckoutCreateBranchResponse = z.infer<typeof CheckoutCreateBranchResponseSchema>;
export type StashSaveRequest = z.infer<typeof StashSaveRequestSchema>;
export type StashSaveResponse = z.infer<typeof StashSaveResponseSchema>;
export type StashPopRequest = z.infer<typeof StashPopRequestSchema>;
export type StashPopResponse = z.infer<typeof StashPopResponseSchema>;
export type StashListRequest = z.infer<typeof StashListRequestSchema>;
export type StashListResponse = z.infer<typeof StashListResponseSchema>;
export type StashEntry = z.infer<typeof StashEntrySchema>;
export type ValidateBranchRequest = z.infer<typeof ValidateBranchRequestSchema>;
export type ValidateBranchResponse = z.infer<typeof ValidateBranchResponseSchema>;
export type BranchSuggestionsRequest = z.infer<typeof BranchSuggestionsRequestSchema>;
export type BranchSuggestionsResponse = z.infer<typeof BranchSuggestionsResponseSchema>;
export type GitHubSearchItem = z.infer<typeof GitHubSearchItemSchema>;
export type GitHubSearchKind = z.infer<typeof GitHubSearchKindSchema>;
export type GitHubSearchRequest = z.infer<typeof GitHubSearchRequestSchema>;
export type GitHubSearchResponse = z.infer<typeof GitHubSearchResponseSchema>;
export type GitLabSearchItem = z.infer<typeof GitLabSearchItemSchema>;
export type GitLabSearchKind = z.infer<typeof GitLabSearchKindSchema>;
export type GitLabSearchRequest = z.infer<typeof GitLabSearchRequestSchema>;
export type GitLabSearchResponse = z.infer<typeof GitLabSearchResponseSchema>;
export type CreateGitHubIssueRequest = z.infer<typeof CreateGitHubIssueRequestSchema>;
export type CreateGitHubIssueResponse = z.infer<typeof CreateGitHubIssueResponseSchema>;
export type CreateGitLabIssueRequest = z.infer<typeof CreateGitLabIssueRequestSchema>;
export type CreateGitLabIssueResponse = z.infer<typeof CreateGitLabIssueResponseSchema>;
export type OAuthProvider = z.infer<typeof OAuthProviderSchema>;
export type OAuthInitiateRequest = z.infer<typeof OAuthInitiateRequestSchema>;
export type OAuthInitiateResponse = z.infer<typeof OAuthInitiateResponseSchema>;
export type OAuthCompleteRequest = z.infer<typeof OAuthCompleteRequestSchema>;
export type OAuthCompleteResponse = z.infer<typeof OAuthCompleteResponseSchema>;
export type OAuthStatusRequest = z.infer<typeof OAuthStatusRequestSchema>;
export type OAuthStatusResponse = z.infer<typeof OAuthStatusResponseSchema>;
export type OAuthSignOutRequest = z.infer<typeof OAuthSignOutRequestSchema>;
export type OAuthSignOutResponse = z.infer<typeof OAuthSignOutResponseSchema>;
export type CreateAppostleWorktreeRequest = z.infer<typeof CreateAppostleWorktreeRequestSchema>;
export type DirectorySuggestionsRequest = z.infer<typeof DirectorySuggestionsRequestSchema>;
export type DirectorySuggestionsResponse = z.infer<typeof DirectorySuggestionsResponseSchema>;
export type ListDirectoryRequest = z.infer<typeof ListDirectoryRequestSchema>;
export type ListDirectoryResponse = z.infer<typeof ListDirectoryResponseSchema>;
export type ListFilesystemRootsRequest = z.infer<typeof ListFilesystemRootsRequestSchema>;
export type ListFilesystemRootsResponse = z.infer<typeof ListFilesystemRootsResponseSchema>;
export type AppostleWorktreeListRequest = z.infer<typeof AppostleWorktreeListRequestSchema>;
export type AppostleWorktreeListResponse = z.infer<typeof AppostleWorktreeListResponseSchema>;
export type AppostleWorktreeArchiveRequest = z.infer<typeof AppostleWorktreeArchiveRequestSchema>;
export type AppostleWorktreeArchiveResponse = z.infer<typeof AppostleWorktreeArchiveResponseSchema>;
export type WorkspaceSetupStatusRequest = z.infer<typeof WorkspaceSetupStatusRequestSchema>;
export type ListAvailableEditorsRequest = z.infer<typeof ListAvailableEditorsRequestSchema>;
export type OpenInEditorRequest = z.infer<typeof OpenInEditorRequestSchema>;
export type OpenProjectRequest = z.infer<typeof OpenProjectRequestSchema>;
export type InitRepoRequest = z.infer<typeof InitRepoRequestSchema>;
export type CloneRepoRequest = z.infer<typeof CloneRepoRequestSchema>;
export type ArchiveWorkspaceRequest = z.infer<typeof ArchiveWorkspaceRequestSchema>;
export type FileExplorerRequest = z.infer<typeof FileExplorerRequestSchema>;
export type FileExplorerResponse = z.infer<typeof FileExplorerResponseSchema>;
export type WorkspaceSearchFilesRequest = z.infer<typeof WorkspaceSearchFilesRequestSchema>;
export type WorkspaceSearchFilesResponse = z.infer<typeof WorkspaceSearchFilesResponseSchema>;
export type WorkspaceSearchFilesEntry = z.infer<typeof WorkspaceSearchFilesEntrySchema>;
export type FileWriteRequest = z.infer<typeof FileWriteRequestSchema>;
export type FileWriteResponse = z.infer<typeof FileWriteResponseSchema>;
export type FileDeleteRequest = z.infer<typeof FileDeleteRequestSchema>;
export type FileDeleteResponse = z.infer<typeof FileDeleteResponseSchema>;
export type FileExplorerDeleteRequest = z.infer<typeof FileExplorerDeleteRequestSchema>;
export type FileExplorerDeleteResponse = z.infer<typeof FileExplorerDeleteResponseSchema>;
export type FileMkdirRequest = z.infer<typeof FileMkdirRequestSchema>;
export type FileMkdirResponse = z.infer<typeof FileMkdirResponseSchema>;
export type FileMoveRequest = z.infer<typeof FileMoveRequestSchema>;
export type FileMoveResponse = z.infer<typeof FileMoveResponseSchema>;
export type ProjectIconRequest = z.infer<typeof ProjectIconRequestSchema>;
export type ProjectIconResponse = z.infer<typeof ProjectIconResponseSchema>;
export type ProjectIcon = z.infer<typeof ProjectIconSchema>;
export type FileDownloadTokenRequest = z.infer<typeof FileDownloadTokenRequestSchema>;
export type FileDownloadTokenResponse = z.infer<typeof FileDownloadTokenResponseSchema>;
export type RestartServerRequestMessage = z.infer<typeof RestartServerRequestMessageSchema>;
export type ShutdownServerRequestMessage = z.infer<typeof ShutdownServerRequestMessageSchema>;
export type ClearAgentAttentionMessage = z.infer<typeof ClearAgentAttentionMessageSchema>;
export type ClearAgentAttentionResponseMessage = z.infer<
  typeof ClearAgentAttentionResponseMessageSchema
>;
export type ClientHeartbeatMessage = z.infer<typeof ClientHeartbeatMessageSchema>;
export type ListCommandsRequest = z.infer<typeof ListCommandsRequestSchema>;
export type ListCommandsResponse = z.infer<typeof ListCommandsResponseSchema>;
export type SkillProvider = z.infer<typeof SkillProviderSchema>;
export type SkillScope = z.infer<typeof SkillScopeSchema>;
export type SkillFrontmatter = z.infer<typeof SkillFrontmatterSchema>;
export type ListSkillsRequest = z.infer<typeof ListSkillsRequestSchema>;
export type ListSkillsResponse = z.infer<typeof ListSkillsResponseSchema>;
export type CreateSkillRequest = z.infer<typeof CreateSkillRequestSchema>;
export type CreateSkillResponse = z.infer<typeof CreateSkillResponseSchema>;
export type WriteSkillFrontmatterRequest = z.infer<typeof WriteSkillFrontmatterRequestSchema>;
export type WriteSkillFrontmatterResponse = z.infer<typeof WriteSkillFrontmatterResponseSchema>;
export type RegisterPushTokenMessage = z.infer<typeof RegisterPushTokenMessageSchema>;
export type UnregisterPushTokenMessage = z.infer<typeof UnregisterPushTokenMessageSchema>;
export type RegisterWebPushSubscriptionMessage = z.infer<
  typeof RegisterWebPushSubscriptionMessageSchema
>;
export type UnregisterWebPushSubscriptionMessage = z.infer<
  typeof UnregisterWebPushSubscriptionMessageSchema
>;
export type UpdateNotificationPreferencesMessage = z.infer<
  typeof UpdateNotificationPreferencesMessageSchema
>;
export type SendTestWebPushRequest = z.infer<typeof SendTestWebPushRequestSchema>;
export type SendTestPushRequest = z.infer<typeof SendTestPushRequestSchema>;

// Terminal message types
export type ListTerminalsRequest = z.infer<typeof ListTerminalsRequestSchema>;
export type ListTerminalsResponse = z.infer<typeof ListTerminalsResponseSchema>;
export type SubscribeTerminalsRequest = z.infer<typeof SubscribeTerminalsRequestSchema>;
export type UnsubscribeTerminalsRequest = z.infer<typeof UnsubscribeTerminalsRequestSchema>;
export type TerminalsChanged = z.infer<typeof TerminalsChangedSchema>;
export type CreateTerminalRequest = z.infer<typeof CreateTerminalRequestSchema>;
export type CreateTerminalResponse = z.infer<typeof CreateTerminalResponseSchema>;
export type StartWorkspaceScriptRequest = z.infer<typeof StartWorkspaceScriptRequestSchema>;
export type StartWorkspaceScriptResponse = z.infer<
  typeof StartWorkspaceScriptResponseMessageSchema
>;
export type SubscribeTerminalRequest = z.infer<typeof SubscribeTerminalRequestSchema>;
export type SubscribeTerminalResponse = z.infer<typeof SubscribeTerminalResponseSchema>;
export type UnsubscribeTerminalRequest = z.infer<typeof UnsubscribeTerminalRequestSchema>;
export type TerminalInput = z.infer<typeof TerminalInputSchema>;
export type TerminalCell = z.infer<typeof TerminalCellSchema>;
export type TerminalCursorStyle = z.infer<typeof TerminalCursorStyleSchema>;
export type TerminalCursor = z.infer<typeof TerminalCursorSchema>;
export type TerminalState = z.infer<typeof TerminalStateSchema>;
export type CloseItemsRequest = z.infer<typeof CloseItemsRequestMessageSchema>;
export type CloseItemsResponse = z.infer<typeof CloseItemsResponseSchema>;
export type KillTerminalRequest = z.infer<typeof KillTerminalRequestSchema>;
export type KillTerminalResponse = z.infer<typeof KillTerminalResponseSchema>;
export type CaptureTerminalRequest = z.infer<typeof CaptureTerminalRequestSchema>;
export type CaptureTerminalResponse = z.infer<typeof CaptureTerminalResponseSchema>;
export type TerminalStreamExit = z.infer<typeof TerminalStreamExitSchema>;

// ============================================================================
// WebSocket Level Messages (wraps session messages)
// ============================================================================

// WebSocket-only messages (not session messages)
export const WSPingMessageSchema = z.object({
  type: z.literal("ping"),
});

export const WSPongMessageSchema = z.object({
  type: z.literal("pong"),
});

export const WSHelloMessageSchema = z.object({
  type: z.literal("hello"),
  clientId: z.string().min(1),
  clientType: z.enum(["mobile", "browser", "cli", "mcp"]),
  protocolVersion: z.number().int(),
  appVersion: z.string().optional(),
  capabilities: z
    .object({
      voice: z.boolean().optional(),
      pushNotifications: z.boolean().optional(),
    })
    .passthrough()
    .optional(),
  /** Auth-server userId of the connecting user. Used for per-user Claude profile selection. */
  userId: z.string().optional(),
  /** Display name of the connecting user (for profile dir naming). */
  username: z.string().optional(),
  /**
   * Short-lived EdDSA JWT minted by the auth-server (`POST /auth/ws-token`).
   * Required for all authenticated connections — the daemon verifies the
   * signature locally using the pinned server pubkey and extracts `ownerUserId`
   * from the `sub` claim. Clean cut: no allow-list fallback.
   */
  wsToken: z.string(),
  /**
   * Optional connection mode discriminator. Defaults to `"session"` (the
   * existing JSON-RPC-over-WS app-protocol path: the connection is wired into a
   * Session/agent context).
   *
   * `"tcp-forward"` is the cross-host SSH dialer mode: after the hello is
   * accepted (verifyWsJwt + access check + loopback target gate), the daemon
   * does NOT allocate a Session — it opens a raw TCP socket to `target` and
   * pumps bytes bidirectionally between that socket and the encrypted relay
   * channel. The connection has no agent, no timeline, no `sessions` map
   * entry.
   *
   * v1 gate: `target.host` MUST be `127.0.0.1` and `target.port` MUST be `22`.
   * Anything else is rejected. This shape is fixed forward-compat for future
   * generalisation to arbitrary loopback ports.
   *
   * `"host-exec"` is the cross-host terminal mode: after the hello is accepted
   * (same verifyWsJwt + access check as tcp-forward), the daemon spawns a shell
   * running `exec.command`, streams stdout/stderr back as JSON frames, and
   * sends a final `{ type: "exit", code }` frame. No Session, no agent, no TCP
   * socket — see `cross-host/host-exec-manager.ts`. Drives the full-trust shell
   * an org member already has via the in-app terminal, but from a sibling host.
   *
   * Omitting `mode` is backward-compat: pre-tcp-forward clients (every shipped
   * mobile/browser/CLI build today) just get session mode as before.
   */
  mode: z.enum(["session", "tcp-forward", "host-exec", "host-transfer"]).optional(),
  target: z
    .object({
      host: z.string(),
      port: z.number().int().min(1).max(65535),
    })
    .optional(),
  /**
   * Present only when `mode === "host-exec"`. The shell command to run on this
   * (the accept-side) host, with an optional working directory and a timeout
   * cap. The accept side runs `bash -lc <command>` and streams the output back.
   */
  exec: z
    .object({
      command: z.string().min(1),
      cwd: z.string().optional(),
      timeoutMs: z.number().int().positive().optional(),
    })
    .optional(),
  /**
   * Present only when `mode === "host-transfer"`. One file moves per
   * connection: `get` streams `path` from this host to the dialer, `put`
   * streams the dialer's file into `path` (size + sha256 announced up front,
   * verified after assembly). See `cross-host/host-transfer-manager.ts`.
   */
  transfer: z
    .object({
      op: z.enum(["get", "put"]),
      path: z.string().min(1),
      size: z.number().int().nonnegative().optional(),
      sha256: z.string().optional(),
    })
    .optional(),
});

export const WSRecordingStateMessageSchema = z.object({
  type: z.literal("recording_state"),
  isRecording: z.boolean(),
});

// Wrapped session message
export const WSSessionInboundSchema = z.object({
  type: z.literal("session"),
  message: SessionInboundMessageSchema,
});

export const WSSessionOutboundSchema = z.object({
  type: z.literal("session"),
  message: SessionOutboundMessageSchema,
});

// Complete WebSocket message schemas
export const WSInboundMessageSchema = z.discriminatedUnion("type", [
  WSPingMessageSchema,
  WSHelloMessageSchema,
  WSRecordingStateMessageSchema,
  WSSessionInboundSchema,
]);

export const WSOutboundMessageSchema = z.discriminatedUnion("type", [
  WSPongMessageSchema,
  WSSessionOutboundSchema,
]);

export type WSInboundMessage = z.infer<typeof WSInboundMessageSchema>;
export type WSOutboundMessage = z.infer<typeof WSOutboundMessageSchema>;
export type WSHelloMessage = z.infer<typeof WSHelloMessageSchema>;

// ============================================================================
// Helper functions for message conversion
// ============================================================================

/**
 * Extract session message from WebSocket message
 * Returns null if message should be handled at WS level only
 */
export function extractSessionMessage(wsMsg: WSInboundMessage): SessionInboundMessage | null {
  if (wsMsg.type === "session") {
    return wsMsg.message;
  }
  // Ping and recording_state are WS-level only
  return null;
}

/**
 * Wrap session message in WebSocket envelope
 */
export function wrapSessionMessage(sessionMsg: SessionOutboundMessage): WSOutboundMessage {
  return {
    type: "session",
    message: sessionMsg,
  };
}

export function parseServerInfoStatusPayload(payload: unknown): ServerInfoStatusPayload | null {
  const parsed = ServerInfoStatusPayloadSchema.safeParse(payload);
  if (!parsed.success) {
    return null;
  }
  return parsed.data;
}
